/* eslint-disable no-mixed-operators */
/* eslint-disable @typescript-eslint/no-redeclare */
import { Vector3 } from 'three';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { HsbProject, DataStoreType } from './hsb-project';
import {
    Group,
    Beam,
    BlockReference,
    CollectionEntity,
    COLLECTIONENTITY,
    CoordSys,
    Dimension,
    FastenerAssemblyEnt,
    GenericModel,
    Grid,
    HsbElement,
    HsbEntity,
    HsbModel,
    IFCObjectType,
    MassElement,
    MassGroup,
    MetalPartCollectionEnt,
    MultiWall,
    Nesting,
    newGuid,
    NodeType,
    NonHsb,
    ObjectType,
    Property,
    RefItemType,
    Sheet,
    Sip,
    Stack,
    Truck,
    TruckItem,
    TruckLayer,
    TrussEntity,
    TslInst,
    Units,
    ElementWall,
    ElementWallSIP,
    ElementRoof,
    ElementRoofSIP,
    Opening,
    Node,
    TruckStack,
} from './hsbcad-model';

import * as THREE from 'three';
import { NodeLoaderService } from './node-loader.service';

export class ProjectLoader {
    _treeconfig: any;
    _nodeloader!: NodeLoaderService;
    activeModelId: any;

    initialize(nodeloader: NodeLoaderService) {
        this._treeconfig = null;
        this._nodeloader = nodeloader;
        this.activeModelId = null;
    }

    createNoGroup(hsbProject: HsbProject, hsbModel: HsbModel) {
        let foundNoGroup = hsbModel.Groups.filter((e) => e.house === 'NoGroup')[0];

        if (!foundNoGroup) {
            var noGroup = new Group(0);
            noGroup.house = 'NoGroup';
            noGroup.handle = newGuid();
            noGroup.hsbmodel = hsbModel;
            hsbModel.Groups.push(noGroup);
            this.addEntityToModel(hsbProject, hsbModel, noGroup);
            return noGroup;
        }
        return foundNoGroup;
    }

    prepareHsbModel(hsbModel: HsbModel) {
        // If there are no groups define and we have enities we need to create a default group to attach these to.
        hsbModel.Entities.forEach((entity) => {
            // process elements...
            if (entity instanceof HsbElement) {
                var elemententities = entity.getEntities();
                // Add objects for walls and sheets arrays.
                entity.sheets = [];
                entity.beams = [];
                entity.openings = [];
                entity.entities = elemententities;
                // Put in correct group based on type
                elemententities?.forEach((childEntity) => {
                    if (childEntity.groups.length === 0) {
                        childEntity.groups.push(entity.group);
                    }
                    if (childEntity instanceof Sheet) {
                        entity.sheets.push(childEntity);
                    } else if (childEntity instanceof Opening) {
                        entity.openings.push(childEntity);
                    } else if (childEntity instanceof Beam) {
                        entity.beams.push(childEntity);
                    }
                });
            }
        });
    }

    checkforNesting(hsbModel: HsbModel, hsbProject: HsbProject) {
        let nestingChilds = hsbModel.Entities.filter(function (e) {
            return e.props['hsb_nestingchild.parentuid'];
        });
        let nestings: Nesting[] = hsbModel.Entities.filter(function (e) {
            return e instanceof Nesting;
        }) as Nesting[];

        nestings.forEach((nesting: Nesting) => {
            let entitiesFound = nestingChilds.filter(function (e) {
                return e.props['hsb_nestingchild.parentuid'].value === nesting.handle;
            });
            nesting.nestingChilds = entitiesFound;
            entitiesFound.forEach((entity: any) => {
                let childEntity = hsbModel.Entities.filter(function (e) {
                    return entity.props['hsb_nestingchild.entity'].value === e.handle;
                });
                entity.modelEntity = childEntity;
            });
        });
    }

    private groupBy(array: any[], f: (...args: any) => any) {
        var groups: { [key: string]: any[] } = {};
        array.forEach(function (o) {
            var group = JSON.stringify(f(o));
            groups[group] = groups[group] || [];
            groups[group].push(o);
        });
        return Object.keys(groups).map(function (group) {
            return groups[group];
        });
    }

    checkforMultiWalls(hsbModel: HsbModel, hsbProject: HsbProject) {
        let multiWallChilds = hsbModel.Entities.filter(function (e) {
            return e.props['hsb_multiwall.number'];
        });

        var multiWalls = this.groupBy(multiWallChilds, function (item) {
            return [item.props['hsb_multiwall.number']];
        });

        multiWalls.forEach((multiWall) => {
            let multiwallEntity = new MultiWall(0);
            let ent = multiWall[0];

            let parentUid = ent.propertyValue('hsb_multiwall.number');
            if (typeof parentUid === 'undefined') return;

            multiwallEntity.handle = parentUid;
            multiwallEntity.addProperty(
                new Property('handle', 'Data', parentUid, true, 'Property.Handle', 'Property.Data')
            );
            multiwallEntity.addProperty(ent.props['hsb_multiwall.number']);
            multiwallEntity.hsbmodel = hsbModel;
            this.addEntityToModel(hsbProject, hsbModel, multiwallEntity);

            multiWall.forEach((multiWallElement) => {
                if (!multiwallEntity.childEntities) {
                    multiwallEntity.childEntities = [];
                }

                multiwallEntity.childEntities.push(multiWallElement);
            });
        });
    }

    checkforTruckLoading(hsbModel: HsbModel, hsbProject: HsbProject) {
        let truckStacks: Stack[] = hsbModel.Entities.filter(function (e) {
            return e instanceof Stack;
        }) as Stack[];
        let truckEntities = hsbModel.Entities.filter(function (e) {
            return e.props['hsb_stackingchild.parentuid'];
        });

        truckStacks.forEach((stack) => {
            let stackId = stack.propertyValue('hsb_stackingparent.myuid');
            if (!stackId) return;
            let entitiesFound = truckEntities.filter(function (e) {
                return e.propertyValue('hsb_stackingchild.parentuid') === stackId;
            });
            stack.nestingChilds = entitiesFound;
        });

        var trucks = this.groupBy(truckStacks, function (item) {
            return [item.propertyValue('parentuid')];
        });

        trucks.forEach((truckarr) => {
            let truckEntity = new Truck(0);
            let ent = truckarr[0];
            truckEntity.handle = 'truck' + ent.handle;
            truckEntity.addProperty(
                new Property('handle', 'Data', ent.handle, true, 'Property.Handle', 'Property.Data')
            );
            truckEntity.hsbmodel = hsbModel;
            this.addEntityToModel(hsbProject, hsbModel, truckEntity);
            truckarr.forEach((stack) => {
                if (stack) {
                    if (!truckEntity.truckStacks) {
                        truckEntity.truckStacks = [];
                    }

                    truckEntity.truckStacks.push(stack);
                }
            });
        });
    }

    getFilterInfo(hsbProject: HsbProject) {
        // Get config
        var dataroot = hsbProject.nodeModel.findGroupChildNode(hsbProject.nodeModel.root, 'data');
        var confignnode = hsbProject.nodeModel.getconfigNode();

        if (dataroot && confignnode?.metadata?.data?.treeconfig) {
            var modeltreeconfig = confignnode.metadata.data._treeconfig;

            var allnodes: Node[] = [];
            if (modeltreeconfig) {
                modeltreeconfig.nodeinclude.forEach(function (nodeinclude: any) {
                    var foundnodes: Node[] = [];

                    if (nodeinclude.include && nodeinclude.searchnode) {
                        nodeinclude.searchnode.forEach(function (searchnode: any) {
                            var found = hsbProject.nodeModel.getNodesByEntityProperty(
                                dataroot as Node,
                                searchnode.objecttype,
                                searchnode.propertyname,
                                searchnode.propertyvalue,
                                searchnode.matchtype
                            );
                            if (
                                found.length > 0 &&
                                searchnode.nodename &&
                                searchnode.nodename.length > 0
                            ) {
                                hsbProject.nodeModel.setNodeNameFromEntityPropertyValue(
                                    found,
                                    searchnode
                                );
                            }
                            foundnodes = foundnodes.concat(found);
                            allnodes = allnodes.concat(foundnodes);
                        });
                    }
                });

                hsbProject.nodeModel.filteredNodes = allnodes;
            }
        }
    }

    createIFCMMNodeModel(hsbModel: HsbModel, hsbProject: HsbProject) {
        var dataroot = hsbProject.nodeModel.findGroupChildNode(hsbProject.nodeModel.root, 'data');
        var datarootmodel;

        if (dataroot?.children && dataroot.children.length > 0) {
            for (var i = 0; i < 2; i++) {
                if (dataroot.children[i].metadata.data.itemtype === NodeType.Model) {
                    datarootmodel = dataroot.children[i];
                    break;
                }
            }
        }

        if (datarootmodel) {
            // inside the drawing name and make this the root node.
            let drawingnode = hsbProject.nodeModel.createNode(hsbModel.DrawingName, datarootmodel);
            drawingnode.metadata.data = {
                origin: 'hsbcad',
                nodetype: NodeType.Geom,
                refitemtype: RefItemType.Parent,
            };
            drawingnode.groupsite = 'site';
            hsbProject.nodeModel.addChildNode(datarootmodel, drawingnode);
            drawingnode.hsbmodel = hsbModel;
            drawingnode.entity = new NonHsb('site');
            drawingnode.entity.hsbmodel = drawingnode.hsbmodel;
            drawingnode.entity.node = drawingnode;
            drawingnode.pathid = 'site';

            let drawingroot = drawingnode;

            return drawingroot;
        }

        return null;
    }

    /**
     * Removes all nodes from the given model for which there is no geometry or for nodes that are of type TslInst, BlockRef or NonHsb.
     * The prune will keep nodes if they have children that are relevant.
     * @param hsbModel
     * @param hsbProject
     * @returns
     */
    removeEmptyGroupNodes(hsbProject: HsbProject) {
        // We dont filted IFC models
        let self = this;
        Object.keys(hsbProject.HsbModels).forEach(function (modelKey) {
            let hsbModel = hsbProject.HsbModels[modelKey];
            if (hsbModel.projectSource === 'ifc') return;

            let drawingroot = hsbProject.nodeModel.findGroupSiteRootNode(
                hsbProject.nodeModel.ModelRoot,
                hsbModel.modelId
            );
            // No need to prune if root has no children
            if (!drawingroot?.children) return;

            if (!hsbModel.geomBuffers) {
                return;
            }
            self.prune(drawingroot, 0, hsbModel, hsbProject);
        });
    }
    /**
     * prunes the tree from any branch that doesnt have any geom.
     * @param node Starting node from which to test
     * @param depth Current depth from the starting node
     * @returns True if a node should be pruned.
     */
    prune(node: any, depth: number, hsbModel: any, hsbProject: HsbProject): boolean {
        // IF children we iterate over them first. If a child should not be pruned we want to keep the parent.
        if (node.children?.length > 0) {
            for (let i = 0; i < node.children.length; ++i) {
                // Check if child was to be deleted
                let childPrune = this.prune(node.children[i], depth + 1, hsbModel, hsbProject);
                //Deletion act of child
                if (childPrune) {
                    if (node.children[i].entity) {
                        let entityIndex = hsbModel.findEntityIndex(node.children[i].entity.handle);
                        if (entityIndex > -1) {
                            // Remove from entities in model
                            hsbModel.Entities.splice(entityIndex, 1);
                            //Remove from entitytable
                            delete hsbProject.EntityTable[hsbModel.modelId][
                                node.children[i].entity.handle
                            ];
                        }
                    }
                    //Remove as child
                    node.children.splice(i, 1);
                    --i;
                }
            }
        }
        // IF nodetype is Geom we still need to check if the buffers actually contain geometry (e.g. TslInst and Blocks can but dont need to have geometry).
        let hasGeom = false;
        if (node.metadata?.data?.nodetype === NodeType.Geom) {
            hsbModel.geomBuffers.forEach((geomBuffer: { data: { [x: string]: any } }) => {
                hasGeom = hasGeom || geomBuffer.data[node.entity.handle];
            });
        }
        // Filter out tslinst, blockrefs and nonhsb
        let filteredType = node.entity instanceof NonHsb;
        // Check if the node has no children left
        let hasNoChildren = !node.children || node.children.length === 0;
        // Pass the mark for deletion (no geom or is a filtered type AND no children left after prune
        return (!hasGeom || filteredType) && hasNoChildren;
    }

    createMMNodeModel(hsbModel: HsbModel, hsbProject: HsbProject) {
        if (hsbProject.projectSource === 'ifc') {
            // Get out - Node model already created if an IFC source map.
            return;
        }

        // Add the LMV into the "model" node.
        var dataroot = hsbProject.nodeModel.findGroupChildNode(hsbProject.nodeModel.root, 'data');
        var datarootmodel;

        if (dataroot?.children && dataroot.children.length > 0) {
            for (var i = 0; i < 2; i++) {
                if (dataroot.children[i].metadata.data.itemtype === NodeType.Model) {
                    datarootmodel = dataroot.children[i];
                    break;
                }
            }
        }
        if (datarootmodel) {
            // inside the drawing name and make this the root node.
            let drawingnode = hsbProject.nodeModel.createNode(hsbModel.DrawingName, datarootmodel);
            drawingnode.metadata.data = {
                origin: 'hsbcad',
                nodetype: NodeType.Geom,
                refitemtype: RefItemType.Parent,
            };
            drawingnode.groupsite = 'site';
            hsbProject.nodeModel.addChildNode(datarootmodel, drawingnode);
            drawingnode.hsbmodel = hsbModel;
            drawingnode.entity = new NonHsb('site');
            drawingnode.entity.node = drawingnode;
            drawingnode.entity.hsbmodel = drawingnode.hsbmodel!;
            drawingnode.pathid = 'site';

            // let orphanageNode = hsbProject.nodeModel.createGroupNode(drawingnode,'Orphans' );
            // hsbProject.nodeModel.addChildNode(drawingnode, orphanageNode);
            let drawingroot = drawingnode;

            var addSubGroup = function (entity: HsbEntity) {
                entity.groups.forEach((group) => {
                    var groupnode = hsbProject.nodeModel.findGroupNode(drawingroot, group);
                    if (!groupnode) {
                        return;
                    }

                    var elementsubnode = hsbProject.nodeModel.findGroupChildNode(
                        groupnode,
                        entity.type
                    );
                    if (!elementsubnode) {
                        elementsubnode = hsbProject.nodeModel.createNode(entity.type, groupnode);
                        elementsubnode.subgroup = entity.type;
                        elementsubnode.hsbmodel = hsbModel;
                        elementsubnode.entity = new NonHsb('site');
                        if (elementsubnode.hsbmodel)
                            elementsubnode.entity.hsbmodel = elementsubnode.hsbmodel;
                        elementsubnode.entity.node = elementsubnode;
                        hsbProject.nodeModel.addChildNode(groupnode, elementsubnode);
                    }

                    var entitynode = hsbProject.nodeModel.createNode(
                        entity.getDislayName(),
                        elementsubnode
                    );
                    entitynode.entity = entity;
                    entitynode.hsbmodel = hsbModel;

                    entitynode.pathid = entity.handle.toLowerCase();
                    entitynode.metadata.data = {
                        nodetype: NodeType.Geom,
                        itemtype: NodeType.Geom,
                        refitemtype: RefItemType.Parent,
                        geom: {},
                    };
                    entitynode.entity.node = entitynode;
                    hsbProject.nodeModel.addChildNode(elementsubnode, entitynode);
                });
            };

            // Create all groups
            hsbModel.Groups.forEach((group: Group) => {
                var groupnode = hsbProject.nodeModel.createGroupNode(drawingroot, group);
                if (groupnode) {
                    groupnode.pathid = group.handle.toLowerCase();
                    groupnode.hsbmodel = hsbModel;
                    groupnode.metadata.data = {
                        nodetype: NodeType.Geom,
                        itemtype: NodeType.Geom,
                        refitemtype: RefItemType.Parent,
                    };
                    groupnode.entity = new NonHsb(groupnode.pathid);
                    groupnode.entity.hsbmodel = groupnode.hsbmodel!;

                    groupnode.entity.node = groupnode;
                }
            });

            hsbModel.Entities.forEach((entity: HsbEntity | null) => {
                if (entity instanceof HsbElement) {
                    var element = entity;
                    var groupnode =
                        hsbProject.nodeModel.findGroupNode(drawingroot, element.group) ??
                        hsbProject.nodeModel.createGroupNode(drawingroot, element.group);
                    if (!groupnode) return;

                    if (!element.group.element) {
                        // No group we need to create a new element node off this group node.
                        let elementnode = hsbProject.nodeModel.createNode(entity.type, groupnode);
                        hsbProject.nodeModel.addChildNode(groupnode, elementnode);
                        elementnode.name =
                            typeof element.number === 'string'
                                ? element.number
                                : element.number?.toString() ?? '';
                        groupnode = elementnode;
                    }
                    groupnode.entity = element;
                    groupnode.hsbmodel = hsbModel;
                    groupnode.pathid = element.handle.toLowerCase();
                    groupnode.metadata.data = {
                        nodetype: NodeType.Geom,
                        itemtype: NodeType.Geom,
                        refitemtype: RefItemType.Parent,
                        geom: {},
                    };
                    groupnode.entity.node = groupnode;

                    // add into entities for this element.
                    var entities = element.entities;

                    entities.forEach((elemententity) => {
                        var elementsubnode = hsbProject.nodeModel.findGroupChildNode(
                            groupnode!,
                            elemententity.type
                        );
                        if (!elementsubnode) {
                            elementsubnode = hsbProject.nodeModel.createNode(
                                elemententity.type,
                                groupnode
                            );
                            elementsubnode.subgroup = elemententity.type;
                            elementsubnode.hsbmodel = hsbModel;
                            elementsubnode.entity = new NonHsb('site');
                            elementsubnode.entity.hsbmodel = elementsubnode.hsbmodel!;
                            elementsubnode.entity.node = elementsubnode;

                            hsbProject.nodeModel.addChildNode(groupnode!, elementsubnode);
                        }

                        var entitynode = hsbProject.nodeModel.createNode(
                            elemententity.getDislayName(),
                            elementsubnode
                        );
                        entitynode.entity = elemententity;
                        entitynode.hsbmodel = hsbModel;
                        entitynode.pathid = elemententity.handle.toLowerCase();
                        entitynode.metadata.data = {
                            nodetype: NodeType.Geom,
                            itemtype: NodeType.Geom,
                            refitemtype: RefItemType.Parent,
                            geom: {},
                        };
                        entitynode.entity.node = entitynode;

                        hsbProject.nodeModel.addChildNode(elementsubnode, entitynode);
                    });
                } else if (entity instanceof HsbEntity && !entity.elementhandle) {
                    // Special case for bomlink tsl inventory items
                    if (
                        entity instanceof TslInst &&
                        entity.scriptname?.toLowerCase() === 'bomlinkinventoryitem'
                    ) {
                        // Just add these inv item to the current group and change the name to the bomitemkey property field
                        if (entity.props.bomlinkitemkey) {
                            entity.groups.forEach((group) => {
                                var groupnode =
                                    hsbProject.nodeModel.findGroupNode(drawingroot, group) ??
                                    hsbProject.nodeModel.createGroupNode(drawingroot, group);
                                if (!groupnode) return;
                                var entitynode = hsbProject.nodeModel.createNode(
                                    entity.props.bomlinkitemkey.value,
                                    groupnode
                                );

                                entitynode.entity = entity;
                                entitynode.hsbmodel = hsbModel;

                                entitynode.pathid = entity.handle.toLowerCase();
                                entitynode.metadata.data = {
                                    nodetype: NodeType.Geom,
                                    itemtype: NodeType.Geom,
                                    refitemtype: RefItemType.Parent,
                                    geom: {},
                                };
                                entitynode.entity.node = entitynode;

                                hsbProject.nodeModel.addChildNode(groupnode, entitynode);

                                groupnode = entitynode;

                                if (entity.entities) {
                                    entity.entities.forEach(function (e) {
                                        var entity =
                                            hsbProject.EntityTable[hsbModel.modelId][e.handle];

                                        var elementsubnode =
                                            hsbProject.nodeModel.findGroupChildNode(
                                                groupnode!,
                                                entity.type
                                            );
                                        if (!elementsubnode) {
                                            elementsubnode = hsbProject.nodeModel.createNode(
                                                entity.type,
                                                groupnode
                                            );
                                            elementsubnode.subgroup = entity.type;
                                            elementsubnode.hsbmodel = hsbModel;

                                            hsbProject.nodeModel.addChildNode(
                                                groupnode!,
                                                elementsubnode
                                            );
                                        }

                                        var entitynode = hsbProject.nodeModel.createNode(
                                            entity.getDislayName(),
                                            elementsubnode
                                        );
                                        entitynode.entity = entity;
                                        entitynode.hsbmodel = hsbModel;

                                        entitynode.pathid = entity.handle.toLowerCase();
                                        entitynode.metadata.data = {
                                            nodetype: NodeType.Geom,
                                            itemtype: NodeType.Geom,
                                            refitemtype: RefItemType.Parent,
                                            geom: {},
                                        };
                                        entitynode.entity.node = entitynode;

                                        hsbProject.nodeModel.addChildNode(
                                            elementsubnode,
                                            entitynode
                                        );
                                    });
                                }
                            });
                        }
                        // } else if (entity instanceof Truck && entity.truckLayers) {

                        //   var trucksNode = hsbProject.nodeModel.findGroupChildNode(datarootmodel, 'Trucks');

                        //   if (!trucksNode) {
                        //     trucksNode = hsbProject.nodeModel.createNode('Trucks', drawingroot);
                        //     hsbProject.nodeModel.addChildNode(drawingroot, trucksNode);
                        //     trucksNode.hsbmodel = hsbModel;
                        //     trucksNode.entity = new NonHsb('trucks');
                        //     trucksNode.entity.node = trucksNode;
                        //     trucksNode.metadata.data = { nodetype: NodeType.Geom, itemtype: NodeType.Geom, refitemtype: RefItemType.Parent, geom: {} };
                        //     trucksNode.entity.hsbmodel = trucksNode.hsbmodel;
                        //     trucksNode.pathid = 'trucks';
                        //   }

                        //   var truckNode = hsbProject.nodeModel.createNode('Truck', trucksNode);
                        //   hsbProject.nodeModel.addChildNode(trucksNode, truckNode);
                        //   truckNode.hsbmodel = hsbModel;
                        //   truckNode.entity = entity;
                        //   truckNode.entity.node = truckNode;
                        //   truckNode.metadata.data = { nodetype: NodeType.Geom, itemtype: NodeType.Geom, refitemtype: RefItemType.Parent, geom: {} };
                        //   truckNode.entity.hsbmodel = truckNode.hsbmodel;
                        //   truckNode.pathid = entity.handle.toLowerCase();;

                        //   entity.truckLayers.forEach(truckLayer => {

                        //     var layerNode = hsbProject.nodeModel.createNode('Layer', truckNode);
                        //     hsbProject.nodeModel.addChildNode(truckNode, layerNode);
                        //     layerNode.hsbmodel = hsbModel;
                        //     layerNode.entity = truckLayer;
                        //     layerNode.entity.node = layerNode;
                        //     layerNode.metadata.data = { nodetype: NodeType.Geom, itemtype: NodeType.Geom, refitemtype: RefItemType.Parent, geom: {} };
                        //     layerNode.entity.hsbmodel = layerNode.hsbmodel;
                        //     layerNode.pathid = truckLayer.handle.toLowerCase();

                        //     truckLayer.truckItems.forEach(truckItem => {
                        //       var itemNode = hsbProject.nodeModel.createNode('Item', layerNode);
                        //       hsbProject.nodeModel.addChildNode(layerNode, itemNode);
                        //       itemNode.hsbmodel = hsbModel;
                        //       itemNode.entity = truckItem;
                        //       itemNode.entity.node = itemNode;
                        //       itemNode.metadata.data = { nodetype: NodeType.Geom, itemtype: NodeType.Geom, refitemtype: RefItemType.Parent, geom: {} };
                        //       itemNode.entity.hsbmodel = itemNode.hsbmodel;
                        //       itemNode.pathid = truckItem.handle.toLowerCase();
                        //     });
                        //   });
                    } else if (entity instanceof Truck && entity.truckStacks) {
                        var trucksNode = hsbProject.nodeModel.findGroupChildNode(
                            drawingroot,
                            'Trucks'
                        );

                        if (!trucksNode) {
                            trucksNode = hsbProject.nodeModel.createNode('Trucks', drawingroot);
                            hsbProject.nodeModel.addChildNode(drawingroot, trucksNode);
                            trucksNode.hsbmodel = hsbModel;
                            trucksNode.entity = new NonHsb('trucks');
                            trucksNode.entity.node = trucksNode;
                            trucksNode.metadata.data = {
                                nodetype: NodeType.Geom,
                                itemtype: NodeType.Geom,
                                refitemtype: RefItemType.Parent,
                                geom: {},
                            };
                            trucksNode.entity.hsbmodel = trucksNode.hsbmodel!;
                            trucksNode.pathid = 'trucks';
                        }

                        var truckNode = hsbProject.nodeModel.createNode('Truck', trucksNode);
                        hsbProject.nodeModel.addChildNode(trucksNode, truckNode);
                        truckNode.hsbmodel = hsbModel;
                        truckNode.entity = entity;
                        truckNode.entity.node = truckNode;
                        truckNode.metadata.data = {
                            nodetype: NodeType.Geom,
                            itemtype: NodeType.Geom,
                            refitemtype: RefItemType.Parent,
                            geom: {},
                        };
                        truckNode.entity.hsbmodel = truckNode.hsbmodel!;
                        truckNode.pathid = entity.handle.toLowerCase();

                        entity.truckStacks.forEach((truckStack) => {
                            var bundleNode = hsbProject.nodeModel.createNode('Stack', truckNode);
                            hsbProject.nodeModel.addChildNode(truckNode, bundleNode);
                            bundleNode.hsbmodel = hsbModel;
                            bundleNode.entity = truckStack;
                            bundleNode.entity!.node = bundleNode;
                            bundleNode.metadata.data = {
                                nodetype: NodeType.Geom,
                                itemtype: NodeType.Geom,
                                refitemtype: RefItemType.Parent,
                                geom: {},
                            };
                            bundleNode.entity!.hsbmodel = bundleNode.hsbmodel!;
                            bundleNode.pathid = truckStack.handle.toLowerCase();

                            truckStack.nestingChilds.forEach((nestingChild: HsbEntity) => {
                                var itemNode = hsbProject.nodeModel.createNode(
                                    'TruckItem',
                                    bundleNode
                                );
                                hsbProject.nodeModel.addChildNode(bundleNode, itemNode);
                                itemNode.hsbmodel = hsbModel;
                                itemNode.entity = nestingChild;
                                itemNode.entity!.node = itemNode;
                                itemNode.metadata.data = {
                                    nodetype: NodeType.Geom,
                                    itemtype: NodeType.Geom,
                                    refitemtype: RefItemType.Parent,
                                    geom: {},
                                };
                                itemNode.entity!.hsbmodel = itemNode.hsbmodel!;
                                itemNode.pathid = nestingChild.handle.toLowerCase();
                            });
                        });
                    } else if (
                        entity instanceof MultiWall &&
                        entity.getChildEntities().length > 0
                    ) {
                        var parentNode = hsbProject.nodeModel.findGroupChildNode(
                            drawingroot,
                            'MultiWalls'
                        );

                        if (!parentNode) {
                            parentNode = hsbProject.nodeModel.createNode('MultiWalls', drawingroot);
                            hsbProject.nodeModel.addChildNode(drawingroot, parentNode);
                            parentNode.hsbmodel = hsbModel;
                            parentNode.entity = new NonHsb('multiwalls');
                            parentNode.entity.node = parentNode;
                            parentNode.metadata.data = {
                                nodetype: NodeType.Geom,
                                itemtype: NodeType.Geom,
                                refitemtype: RefItemType.Parent,
                                geom: {},
                            };
                            parentNode.entity.hsbmodel = parentNode.hsbmodel!;
                            parentNode.pathid = 'multiwalls';
                        }

                        let multiWall = hsbProject.nodeModel.createNode(entity.handle, parentNode);
                        hsbProject.nodeModel.addChildNode(parentNode, multiWall);
                        multiWall.hsbmodel = hsbModel;
                        multiWall.entity = entity;
                        multiWall.entity.node = multiWall;
                        multiWall.metadata.data = {
                            nodetype: NodeType.Geom,
                            itemtype: NodeType.Geom,
                            refitemtype: RefItemType.Parent,
                            geom: {},
                        };
                        multiWall.entity.hsbmodel = multiWall.hsbmodel!;
                        multiWall.pathid = 'multiwall';

                        entity.getChildEntities().forEach((childElement: HsbEntity) => {
                            var childElementNode = hsbProject.nodeModel.createNode(
                                'MultiWallElement',
                                multiWall
                            );
                            hsbProject.nodeModel.addChildNode(multiWall, childElementNode);
                            childElementNode.hsbmodel = hsbModel;
                            childElementNode.entity = childElement;
                            childElementNode.entity!.node = childElementNode;
                            childElementNode.metadata.data = {
                                nodetype: NodeType.Geom,
                                itemtype: NodeType.Geom,
                                refitemtype: RefItemType.Parent,
                                geom: {},
                            };
                            childElementNode.entity!.hsbmodel = childElementNode.hsbmodel!;
                            childElementNode.pathid = childElement.handle.toLowerCase();
                        });
                    } else if (entity instanceof Group) {
                        addSubGroup(entity);
                    } else {
                        let typeName = entity.type + 's';
                        var parentNode = hsbProject.nodeModel.findGroupChildNode(
                            drawingroot,
                            typeName
                        );

                        if (!parentNode) {
                            parentNode = hsbProject.nodeModel.createNode(typeName, drawingroot);
                            hsbProject.nodeModel.addChildNode(drawingroot, parentNode);
                            parentNode.hsbmodel = hsbModel;
                            parentNode.entity = new NonHsb(typeName);
                            parentNode.entity.node = parentNode;
                            parentNode.metadata.data = {
                                nodetype: NodeType.Geom,
                                itemtype: NodeType.Geom,
                                refitemtype: RefItemType.Parent,
                                geom: {},
                            };
                            parentNode.entity.hsbmodel = parentNode.hsbmodel!;
                            parentNode.pathid = typeName;
                        }

                        let node = hsbProject.nodeModel.createNode(entity.handle, parentNode);
                        hsbProject.nodeModel.addChildNode(parentNode, node);
                        node.hsbmodel = hsbModel;
                        node.entity = entity;
                        entity.node = node;
                        node.metadata.data = {
                            nodetype: NodeType.Geom,
                            itemtype: NodeType.Geom,
                            refitemtype: RefItemType.Parent,
                            geom: {},
                        };
                        node.entity.hsbmodel = node.hsbmodel!;
                        node.pathid = entity.type;
                    }
                }
            });
        }
    }

    /**
     * Loads all the necessary project info
     * @param project The project from which to load the info
     * @param hsbProject The project to insert the info in
     * @param unit The extracted unit
     * @returns
     */
    loadProjectInfo(project: { arr: any[] }, hsbProject: { projectSource: any }) {
        //var drawingName = {};
        var drawingName: string = '';
        var unit: string = 'mm';
        project.arr.forEach(
            (projectinfo: {
                dwgname: string | null;
                drawingunit: string | null;
                projectsource: string;
            }) => {
                if (projectinfo.dwgname != null) {
                    drawingName = projectinfo.dwgname;
                } else if (projectinfo.drawingunit != null) {
                    unit = projectinfo.drawingunit.toLowerCase();
                } else if (projectinfo.projectsource && projectinfo.projectsource !== '') {
                    hsbProject.projectSource = projectinfo.projectsource.toLowerCase();
                }
            }
        );

        return { drawingName: drawingName, unit: unit };
    }

    checkForHFDM(
        project: { arr: any[] },
        hsbModel: {
            hfdmCommitData: {
                key?: any;
                timeStamp?: any;
                author?: any;
                description?: any;
                changeSets?: any;
            };
            DrawingName: any;
            findEntity: (arg0: any) => any;
        }
    ) {
        //var drawingName = {};

        project.arr.forEach((projectinfo: { nm: string; arr: any[] }) => {
            if (projectinfo.nm && projectinfo.nm.toLowerCase() === 'mapx[]') {
                projectinfo.arr.forEach((mapx: { arr: any[] }) => {
                    let isHFDM = mapx.arr.filter(function (e: { ty: string; nm: string }) {
                        return e.ty === 'key' && e.nm && e.nm.toLowerCase() === 'hfdmcommit';
                    })[0];
                    if (!isHFDM) return;

                    hsbModel.hfdmCommitData = {};
                    hsbModel.hfdmCommitData.key = hsbModel.DrawingName;
                    let changeSets: { [key: string]: any } = {};
                    mapx.arr.forEach(
                        (mapxvals: {
                            utctimestamp: string;
                            author: any;
                            description: any;
                            nm: string;
                            arr: any[];
                        }) => {
                            if (mapxvals.utctimestamp) {
                                let year = mapxvals.utctimestamp.substring(0, 4);
                                let month = mapxvals.utctimestamp.substring(4, 6);
                                let day = mapxvals.utctimestamp.substring(6, 8);
                                let hrs = mapxvals.utctimestamp.substring(8, 10);
                                let mins = mapxvals.utctimestamp.substring(10, 12);
                                let sec = mapxvals.utctimestamp.substring(12, 14);
                                let millsecs = mapxvals.utctimestamp.substring(14, 18);
                                //        0123 45 67 890  12 345
                                //let t = 2018 08 22 1239 10 000;
                                hsbModel.hfdmCommitData.timeStamp = new Date(
                                    [year, month, day, hrs, mins, sec, millsecs].join(' ')
                                );
                            } else if (mapxvals.author) {
                                hsbModel.hfdmCommitData.author = mapxvals.author;
                            } else if (mapxvals.description) {
                                hsbModel.hfdmCommitData.description = mapxvals.description;
                            } else if (mapxvals.nm && mapxvals.nm.toLowerCase() === 'handle[]') {
                                let handlesPresent = mapxvals.arr.filter(function (e: {
                                    handle: any;
                                }) {
                                    return e.handle;
                                })[0];
                                if (!handlesPresent) return;
                                var action = mapxvals.arr.filter(function (e: { ty: string }) {
                                    return e.ty === 'key';
                                })[0];
                                var actionStatus: string = action.nm.toLowerCase();
                                changeSets[actionStatus] = [];

                                mapxvals.arr.forEach((handles: { handle: any }) => {
                                    if (handles.handle) {
                                        changeSets[actionStatus].push(handles.handle);
                                    }
                                });
                            }
                        }
                    );

                    hsbModel.hfdmCommitData.changeSets = changeSets;

                    Object.keys(changeSets).forEach(function (changeKey) {
                        let handles = changeSets[changeKey];

                        handles.forEach((handle: any) => {
                            let foundEntity = hsbModel.findEntity(handle);
                            if (foundEntity) {
                                foundEntity.hfdmAction = changeKey;
                            }
                        });
                    });
                });
            }
        });
    }

    setProjectInfo(project: { arr: any[] }, dwgname: any) {
        project.arr.forEach((projectinfo: { dwgname: null }) => {
            if (projectinfo.dwgname != null) {
                projectinfo.dwgname = dwgname;
                return;
            }
        });
    }

    loadGroups(groups: { arr: any[] }, hsbProject: any, hsbModel: HsbModel) {
        let self = this;

        groups.arr.forEach((group: { nm: string; arr: any[] }) => {
            if (group.nm.toLowerCase() === 'group') {
                var newgroup = new Group(0);

                group.arr.forEach(
                    (groupinfo: {
                        name: string;
                        handle: string | null;
                        floorheight: null;
                        nequivalentstory: null;
                        bisdeliverablecontainer: null;
                    }) => {
                        if (groupinfo.name != null) {
                            var name = groupinfo.name;
                            var splits = name.split('\\');

                            for (var i = 0; i < splits.length; i++) {
                                if (splits[i].length === 0) {
                                    splits.splice(i, 1);
                                }
                            }

                            for (var i = 0; i < splits.length; i++) {
                                if (i === 0) {
                                    newgroup.house = splits[i];
                                } else if (i === 1) {
                                    newgroup.floor = splits[i];
                                } else {
                                    newgroup.element = splits[i];
                                }
                            }
                        } else if (groupinfo.handle != null) {
                            newgroup.handle = groupinfo.handle;
                        } else if (groupinfo.floorheight != null) {
                            newgroup.floorheight = groupinfo.floorheight;
                        } else if (groupinfo.nequivalentstory != null) {
                            newgroup.equivalentstory = groupinfo.nequivalentstory;
                        } else if (groupinfo.bisdeliverablecontainer != null) {
                            newgroup.isdeliverablecontainter = groupinfo.bisdeliverablecontainer;
                        }
                    }
                );

                newgroup.hsbmodel = hsbModel;
                hsbModel.Groups.push(newgroup);
                self.addEntityToModel(hsbProject, hsbModel, newgroup);
            }
        });
    }

    loadCollections(collections: { arr: any[] }, hsbProject: any, hsbModel: HsbModel) {
        var _self = this;
        var blocks = collections.arr.filter(function (e: { nm: string }) {
            return e.nm && e.nm.toLowerCase() === 'block[]';
        })[0];

        if (!blocks) return;

        blocks.arr.forEach(function (block: { arr: any[] }) {
            var entityset = block.arr.filter(function (e: { nm: string }) {
                return e.nm && e.nm.toLowerCase() === 'entityset';
            })[0];

            if (!entityset) return;

            entityset.arr.forEach(function (entity: { nm: string; arr: any[] }) {
                if (entity.nm.toLowerCase() === 'entity') {
                    var found = entity.arr.filter(function (e: { typename: any }) {
                        return e.typename;
                    })[0];

                    if (found && found.typename.toLowerCase() === 'acdb3dsolid') {
                        let hsbentity = new GenericModel(0, '0', 0);
                        _self.setEntityData(hsbentity, entity, hsbModel);
                        _self.addEntityToModel(hsbProject, hsbModel, hsbentity);
                    }
                } else if (entity.nm.toLowerCase() === 'blockreference') {
                    let hsbentity = new BlockReference(0, '0', 0);
                    _self.setBlockReferenceEntityData(hsbentity, entity, hsbModel);
                    _self.addEntityToModel(hsbProject, hsbModel, hsbentity);
                }
            });
        });
    }

    setBodyData(
        hsbentity: {
            bodydata: { coordSys?: any; vertexpts: any; faces: any };
            handle: string;
            initialCoordSys: any;
        },
        entity: { arr: any[] },
        unitFactor: number
    ) {
        hsbentity.bodydata = { vertexpts: [], faces: [] };
        hsbentity.bodydata.coordSys = new CoordSys();

        if (!(hsbentity instanceof HsbElement)) {
            //hsbentity.coordSys = hsbentity.bodydata.coordSys;
            hsbentity.initialCoordSys = hsbentity.bodydata.coordSys;
        }

        entity.arr.forEach(
            (bodydata: {
                nm: string;
                arr: any[];
                ptorg: {
                    x: number | undefined;
                    y: number | undefined;
                    z: number | undefined;
                } | null;
                vecx: {
                    x: number | undefined;
                    y: number | undefined;
                    z: number | undefined;
                } | null;
                vecy: {
                    x: number | undefined;
                    y: number | undefined;
                    z: number | undefined;
                } | null;
                vecz: {
                    x: number | undefined;
                    y: number | undefined;
                    z: number | undefined;
                } | null;
            }) => {
                // Vertex list
                if (bodydata.nm && bodydata.nm.toLowerCase() === 'vertexlist') {
                    if (bodydata.arr) {
                        bodydata.arr.forEach(
                            (vertexpt: {
                                p: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                };
                            }) => {
                                if (vertexpt.p) {
                                    hsbentity.bodydata.vertexpts.push(
                                        new THREE.Vector3(vertexpt.p.x, vertexpt.p.y, vertexpt.p.z)
                                    );
                                }
                            }
                        );
                    }
                }
                // Face list
                if (bodydata.nm && bodydata.nm.toLowerCase() === 'facelist') {
                    if (bodydata.arr) {
                        bodydata.arr.forEach((face: { f: string }) => {
                            if (face.f) {
                                var faceindexes = face.f.split(';');
                                faceindexes.pop();
                                var indexes = faceindexes.map(function (e: string) {
                                    return parseInt(e);
                                });
                                var allsame = indexes.filter(function (e: any) {
                                    return e === indexes[0];
                                });
                                if (allsame.length !== indexes.length) {
                                    hsbentity.bodydata.faces.push(indexes);
                                }
                            }
                        });
                    }
                }

                if (bodydata.ptorg != null) {
                    hsbentity.bodydata.coordSys.ptOrg = new THREE.Vector3(
                        bodydata.ptorg.x,
                        bodydata.ptorg.y,
                        bodydata.ptorg.z
                    ).multiplyScalar(unitFactor);
                } else if (bodydata.vecx != null) {
                    hsbentity.bodydata.coordSys.vecX = new THREE.Vector3(
                        bodydata.vecx.x,
                        bodydata.vecx.y,
                        bodydata.vecx.z
                    );
                }
                if (bodydata.vecy != null) {
                    hsbentity.bodydata.coordSys.vecY = new THREE.Vector3(
                        bodydata.vecy.x,
                        bodydata.vecy.y,
                        bodydata.vecy.z
                    );
                }
                if (bodydata.vecz != null) {
                    hsbentity.bodydata.coordSys.vecZ = new THREE.Vector3(
                        bodydata.vecz.x,
                        bodydata.vecz.y,
                        bodydata.vecz.z
                    );
                }
            }
        );
    }

    setCollectionEntityData(
        hsbentity: CollectionEntity,
        entitydata: { nm: string; arr: any[] },
        hsbModel: HsbModel
    ) {
        var _self = this;
        const unitFactor = hsbModel.Units.factor;
        if (entitydata.nm && entitydata.nm.toLowerCase() === 'collectionentity') {
            entitydata.arr.forEach(
                (collectionent: {
                    ptorg: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecx: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecy: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecz: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    nm: string;
                    arr: any[];
                }) => {
                    if (collectionent.ptorg != null) {
                        hsbentity.coordSys.ptOrg = new THREE.Vector3(
                            collectionent.ptorg.x,
                            collectionent.ptorg.y,
                            collectionent.ptorg.z
                        ).multiplyScalar(unitFactor);
                    } else if (collectionent.vecx != null) {
                        hsbentity.coordSys.vecX = new THREE.Vector3(
                            collectionent.vecx.x,
                            collectionent.vecx.y,
                            collectionent.vecx.z
                        );
                    } else if (collectionent.vecy != null) {
                        hsbentity.coordSys.vecY = new THREE.Vector3(
                            collectionent.vecy.x,
                            collectionent.vecy.y,
                            collectionent.vecy.z
                        );
                    } else if (collectionent.vecz != null) {
                        hsbentity.coordSys.vecZ = new THREE.Vector3(
                            collectionent.vecz.x,
                            collectionent.vecz.y,
                            collectionent.vecz.z
                        );
                    } else if (collectionent.nm && collectionent.nm.toLowerCase() === 'toolent') {
                        collectionent.arr.forEach((toolent: any) => {
                            _self.setEntityData(hsbentity, toolent, hsbModel);
                        });
                    }
                }
            );
        }
    }

    setGridEntityData(hsbentity: Grid, entitydata: { nm: string; arr: any[] }, hsbModel: HsbModel) {
        var _self = this;
        const unitFactor = hsbModel.Units.factor;
        if (entitydata.nm && entitydata.nm.toLowerCase() === 'grid') {
            entitydata.arr.forEach(
                (grid: {
                    ptorg: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecx: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecy: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecz: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    dsizex: null;
                    dsizey: null;
                    dtextsize: null;
                    dtextoffset: null;
                    nm: string;
                    arr: any[];
                }) => {
                    if (grid.ptorg != null) {
                        hsbentity.coordSys.ptOrg = new THREE.Vector3(
                            grid.ptorg.x,
                            grid.ptorg.y,
                            grid.ptorg.z
                        ).multiplyScalar(unitFactor);
                    } else if (grid.vecx != null) {
                        hsbentity.coordSys.vecX = new THREE.Vector3(
                            grid.vecx.x,
                            grid.vecx.y,
                            grid.vecx.z
                        );
                    } else if (grid.vecy != null) {
                        hsbentity.coordSys.vecY = new THREE.Vector3(
                            grid.vecy.x,
                            grid.vecy.y,
                            grid.vecy.z
                        );
                    } else if (grid.vecz != null) {
                        hsbentity.coordSys.vecZ = new THREE.Vector3(
                            grid.vecz.x,
                            grid.vecz.y,
                            grid.vecz.z
                        );
                    } else if (grid.dsizex != null) {
                        hsbentity.sizex = grid.dsizex;
                    } else if (grid.dsizey != null) {
                        hsbentity.sizey = grid.dsizey;
                    } else if (grid.dtextsize != null) {
                        hsbentity.textsize = grid.dtextsize;
                    } else if (grid.dtextoffset != null) {
                        hsbentity.textoffset = grid.dtextoffset;
                    } else if (grid.nm && grid.nm.toLowerCase() === 'entity') {
                        _self.setEntityData(hsbentity, grid, hsbModel);
                    } else if (grid.nm && grid.nm.toLowerCase() === 'gridline[]') {
                        var addGridLines = function (
                            gridlinesArr: any[],
                            gridlineIntersectionsArr: any[]
                        ) {
                            gridlinesArr.forEach((gridline: { nm: string; arr: any[] }) => {
                                if (gridline.nm && gridline.nm.toLowerCase() === 'gridline') {
                                    var gridLine: any = {};

                                    gridlineIntersectionsArr.push(gridLine);

                                    gridline.arr.forEach(
                                        (gridData: { doffset: null; coordinate: null }) => {
                                            if (gridData.doffset != null) {
                                                gridLine.offset = gridData.doffset;
                                            } else if (gridData.coordinate != null) {
                                                gridLine.coordinate = gridData.coordinate;
                                            }
                                        }
                                    );
                                }
                            });
                        };

                        grid.arr.forEach((gridLines: { nm: string }) => {
                            if (
                                gridLines.nm &&
                                gridLines.nm.toLowerCase() === 'linexintersections'
                            ) {
                                hsbentity.gridlineXIntersections = [];
                                addGridLines(grid.arr, hsbentity.gridlineXIntersections);
                            } else if (
                                gridLines.nm &&
                                gridLines.nm.toLowerCase() === 'lineyintersections'
                            ) {
                                hsbentity.gridlineYIntersections = [];
                                addGridLines(grid.arr, hsbentity.gridlineYIntersections);
                            }
                        });
                    }
                }
            );
        }
    }

    setOpeningEntityData(
        hsbentity: HsbEntity,
        entitydata: { nm: string; arr: any[] },
        hsbModel: HsbModel
    ) {
        var _self = this;
        const unitFactor = hsbModel.Units.factor;
        if (entitydata.nm?.toLowerCase() === 'opening') {
            entitydata.arr.forEach(
                (prop: {
                    ptorg: { x: number | undefined; y: number | undefined; z: number | undefined };
                    vecx: { x: number | undefined; y: number | undefined; z: number | undefined };
                    vecy: { x: number | undefined; y: number | undefined; z: number | undefined };
                    vecz: { x: number | undefined; y: number | undefined; z: number | undefined };
                    ty: string;
                    parententity: any;
                    sillheight: string;
                    headheight: number;
                    height: number;
                    width: number;
                    framewidth: number;
                    stylename: any;
                    transparentpct: any;
                    color: any;
                    description: any;
                    nm: string;
                }) => {
                    if (prop.ptorg)
                        hsbentity.coordSys.ptOrg = new THREE.Vector3(
                            prop.ptorg.x,
                            prop.ptorg.y,
                            prop.ptorg.z
                        ).multiplyScalar(unitFactor);
                    else if (prop.vecx)
                        hsbentity.coordSys.vecX = new THREE.Vector3(
                            prop.vecx.x,
                            prop.vecx.y,
                            prop.vecx.z
                        );
                    else if (prop.vecy)
                        hsbentity.coordSys.vecY = new THREE.Vector3(
                            prop.vecy.x,
                            prop.vecy.y,
                            prop.vecy.z
                        );
                    else if (prop.vecz)
                        hsbentity.coordSys.vecZ = new THREE.Vector3(
                            prop.vecz.x,
                            prop.vecz.y,
                            prop.vecz.z
                        );
                    else if (prop.ty == 'hdl') hsbentity.handle = prop.parententity;
                    else if (prop.sillheight)
                        hsbentity.addProperty(
                            new Property(
                                'sillHeight',
                                'Data',
                                parseInt(prop.sillheight) * unitFactor,
                                true,
                                'Property.SillHeight',
                                'Property.Data'
                            )
                        );
                    else if (prop.headheight)
                        hsbentity.addProperty(
                            new Property(
                                'headheight',
                                'Data',
                                prop.headheight * unitFactor,
                                true,
                                'Property.HeadHeight',
                                'Property.Data'
                            )
                        );
                    else if (prop.height)
                        hsbentity.addProperty(
                            new Property(
                                'height',
                                'Data',
                                prop.height * unitFactor,
                                true,
                                'Property.Height',
                                'Property.Data'
                            )
                        );
                    else if (prop.width)
                        hsbentity.addProperty(
                            new Property(
                                'width',
                                'Data',
                                prop.width * unitFactor,
                                true,
                                'Property.Width',
                                'Property.Data'
                            )
                        );
                    else if (prop.framewidth)
                        hsbentity.addProperty(
                            new Property(
                                'framewidth',
                                'Data',
                                prop.framewidth * unitFactor,
                                true,
                                'Property.FrameWidth',
                                'Property.Data'
                            )
                        );
                    else if (prop.stylename)
                        hsbentity.addProperty(
                            new Property(
                                'style',
                                'Style',
                                prop.stylename,
                                true,
                                'Property.Style',
                                'Property.Style'
                            )
                        );
                    else if (prop.transparentpct)
                        hsbentity.addProperty(
                            new Property(
                                'transparentpct',
                                'Style',
                                prop.transparentpct,
                                true,
                                'Property.Transparency',
                                'Property.Style'
                            )
                        );
                    else if (prop.color)
                        hsbentity.addProperty(
                            new Property(
                                'color',
                                'Style',
                                prop.color,
                                true,
                                'Property.Color',
                                'Property.Style'
                            )
                        );
                    else if (prop.description)
                        hsbentity.addProperty(
                            new Property(
                                'description',
                                'Info',
                                prop.description,
                                true,
                                'Property.Description',
                                'Property.Info'
                            )
                        );
                    else if (prop.nm?.toLowerCase() === 'entity') {
                        _self.setEntityData(hsbentity, prop, hsbModel);
                    }
                }
            );
        }
    }

    setBlockReferenceEntityData(
        hsbentity: BlockReference,
        entitydata: { nm: string; arr: any[] },
        hsbModel: HsbModel
    ) {
        var _self = this;
        const unitFactor = hsbModel.Units.factor;
        if (entitydata.nm && entitydata.nm.toLowerCase() === 'blockreference') {
            entitydata.arr.forEach((blockreference: any) => {
                if (blockreference.blockref != null) {
                    hsbentity.blockref = blockreference.blockref;
                } else if (blockreference.ptorg != null) {
                    hsbentity.coordSys.ptOrg = new THREE.Vector3(
                        blockreference.ptorg.x,
                        blockreference.ptorg.y,
                        blockreference.ptorg.z
                    ).multiplyScalar(unitFactor);
                } else if (blockreference.vecx != null) {
                    hsbentity.coordSys.vecX = new THREE.Vector3(
                        blockreference.vecx.x,
                        blockreference.vecx.y,
                        blockreference.vecx.z
                    );
                } else if (blockreference.vecy != null) {
                    hsbentity.coordSys.vecY = new THREE.Vector3(
                        blockreference.vecy.x,
                        blockreference.vecy.y,
                        blockreference.vecy.z
                    );
                } else if (blockreference.vecz != null) {
                    hsbentity.coordSys.vecZ = new THREE.Vector3(
                        blockreference.vecz.x,
                        blockreference.vecz.y,
                        blockreference.vecz.z
                    );
                } else if (blockreference.nm && blockreference.nm.toLowerCase() === 'entity') {
                    _self.setEntityData(hsbentity, blockreference, hsbModel);
                }
            });
        }
    }

    rgb2hex(rgb: string) {
        if (rgb) {
            let values = rgb.split(',');

            if (values.length === 3) {
                return (
                    '#' +
                    ('0' + parseInt(values[0], 10).toString(16)).slice(-2) +
                    ('0' + parseInt(values[1], 10).toString(16)).slice(-2) +
                    ('0' + parseInt(values[2], 10).toString(16)).slice(-2)
                );
            }
        }
    }

    ACArgbValues = [
        '0,0,0',
        '255,0,0',
        '255,255,0',
        '0,255,0',
        '0,255,255',
        '0,0,255',
        '255,0,255',
        '255,255,255',
        '65,65,65',
        '128,128,128',
        '255,0,0',
        '255,170,170',
        '189,0,0',
        '189,126,126',
        '129,0,0',
        '129,86,86',
        '104,0,0',
        '104,69,69',
        '79,0,0',
        '79,53,53',
        '255,63,0',
        '255,191,170',
        '189,46,0',
        '189,141,126',
        '129,31,0',
        '129,96,86',
        '104,25,0',
        '104,78,69',
        '79,19,0',
        '79,59,53',
        '255,127,0',
        '255,212,170',
        '189,94,0',
        '189,157,126',
        '129,64,0',
        '129,107,86',
        '104,52,0',
        '104,86,69',
        '79,39,0',
        '79,66,53',
        '255,191,0',
        '255,234,170',
        '189,141,0',
        '189,173,126',
        '129,96,0',
        '129,118,86',
        '104,78,0',
        '104,95,69',
        '79,59,0',
        '79,73,53',
        '255,255,0',
        '255,255,170',
        '189,189,0',
        '189,189,126',
        '129,129,0',
        '129,129,86',
        '104,104,0',
        '104,104,69',
        '79,79,0',
        '79,79,53',
        '191,255,0',
        '234,255,170',
        '141,189,0',
        '173,189,126',
        '96,129,0',
        '118,129,86',
        '78,104,0',
        '95,104,69',
        '59,79,0',
        '73,79,53',
        '127,255,0',
        '212,255,170',
        '94,189,0',
        '157,189,126',
        '64,129,0',
        '107,129,86',
        '52,104,0',
        '86,104,69',
        '39,79,0',
        '66,79,53',
        '63,255,0',
        '191,255,170',
        '46,189,0',
        '141,189,126',
        '31,129,0',
        '96,129,86',
        '25,104,0',
        '78,104,69',
        '19,79,0',
        '59,79,53',
        '0,255,0',
        '170,255,170',
        '0,189,0',
        '126,189,126',
        '0,129,0',
        '86,129,86',
        '0,104,0',
        '69,104,69',
        '0,79,0',
        '53,79,53',
        '0,255,63',
        '170,255,191',
        '0,189,46',
        '126,189,141',
        '0,129,31',
        '86,129,96',
        '0,104,25',
        '69,104,78',
        '0,79,19',
        '53,79,59',
        '0,255,127',
        '170,255,212',
        '0,189,94',
        '126,189,157',
        '0,129,64',
        '86,129,107',
        '0,104,52',
        '69,104,86',
        '0,79,39',
        '53,79,66',
        '0,255,191',
        '170,255,234',
        '0,189,141',
        '126,189,173',
        '0,129,96',
        '86,129,118',
        '0,104,78',
        '69,104,95',
        '0,79,59',
        '53,79,73',
        '0,255,255',
        '170,255,255',
        '0,189,189',
        '126,189,189',
        '0,129,129',
        '86,129,129',
        '0,104,104',
        '69,104,104',
        '0,79,79',
        '53,79,79',
        '0,191,255',
        '170,234,255',
        '0,141,189',
        '126,173,189',
        '0,96,129',
        '86,118,129',
        '0,78,104',
        '69,95,104',
        '0,59,79',
        '53,73,79',
        '0,127,255',
        '170,212,255',
        '0,94,189',
        '126,157,189',
        '0,64,129',
        '86,107,129',
        '0,52,104',
        '69,86,104',
        '0,39,79',
        '53,66,79',
        '0,63,255',
        '170,191,255',
        '0,46,189',
        '126,141,189',
        '0,31,129',
        '86,96,129',
        '0,25,104',
        '69,78,104',
        '0,19,79',
        '53,59,79',
        '0,0,255',
        '170,170,255',
        '0,0,189',
        '126,126,189',
        '0,0,129',
        '86,86,129',
        '0,0,104',
        '69,69,104',
        '0,0,79',
        '53,53,79',
        '63,0,255',
        '191,170,255',
        '46,0,189',
        '141,126,189',
        '31,0,129',
        '96,86,129',
        '25,0,104',
        '78,69,104',
        '19,0,79',
        '59,53,79',
        '127,0,255',
        '212,170,255',
        '94,0,189',
        '157,126,189',
        '64,0,129',
        '107,86,129',
        '52,0,104',
        '86,69,104',
        '39,0,79',
        '66,53,79',
        '191,0,255',
        '234,170,255',
        '141,0,189',
        '173,126,189',
        '96,0,129',
        '118,86,129',
        '78,0,104',
        '95,69,104',
        '59,0,79',
        '73,53,79',
        '255,0,255',
        '255,170,255',
        '189,0,189',
        '189,126,189',
        '129,0,129',
        '129,86,129',
        '104,0,104',
        '104,69,104',
        '79,0,79',
        '79,53,79',
        '255,0,191',
        '255,170,234',
        '189,0,141',
        '189,126,173',
        '129,0,96',
        '129,86,118',
        '104,0,78',
        '104,69,95',
        '79,0,59',
        '79,53,73',
        '255,0,127',
        '255,170,212',
        '189,0,94',
        '189,126,157',
        '129,0,64',
        '129,86,107',
        '104,0,52',
        '104,69,86',
        '79,0,39',
        '79,53,66',
        '255,0,63',
        '255,170,191',
        '189,0,46',
        '189,126,141',
        '129,0,31',
        '129,86,96',
        '104,0,25',
        '104,69,78',
        '79,0,19',
        '79,53,59',
        '51,51,51',
        '80,80,80',
        '105,105,105',
        '130,130,130',
        '190,190,190',
        '255,255,255',
    ];

    setEntityData(hsbentity: any, entitydata: any, hsbModel: HsbModel) {
        var _self = this;
        const unitFactor = hsbModel.Units.factor;
        if (
            entitydata.nm &&
            (entitydata.nm.toLowerCase() === 'entity' ||
                entitydata.nm.toLowerCase() === 'ifcentity')
        ) {
            entitydata.arr.forEach((entity: any) => {
                // Object.entries(entity).filter(([key,value] : [string,any])=>key !== 'ty' && value?.nm !== 'IfcEntity').forEach(([key,value])=>{
                //     hsbentity[key] = value;
                //     hsbentity.addProperty(
                //         new Property(
                //             key,
                //             hsbentity instanceof HsbElement ? 'Element info' : 'Entity info',
                //             entity.handle,
                //             true,
                //             `Property.${key}`,
                //             hsbentity instanceof HsbElement ? 'Property.ElementInfo' : 'Property.EntityInfo'
                //         )
                //     );
                // })

                if (entity.handle != null) {
                    hsbentity.handle = entity.handle;
                    hsbentity.addProperty(
                        new Property(
                            'handle',
                            hsbentity instanceof HsbElement ? 'Element info' : 'Entity info',
                            entity.handle,
                            true,
                            'Property.Handle',
                            hsbentity instanceof HsbElement
                                ? 'Property.ElementInfo'
                                : 'Property.EntityInfo'
                        )
                    );
                } else if (entity.ifcid != null) {
                    hsbentity.ifcid = entity.ifcid;
                    hsbentity.addProperty(
                        new Property(
                            'ifcid',
                            hsbentity instanceof HsbElement ? 'Element info' : 'Entity info',
                            hsbentity.ifcid,
                            true,
                            'Property.IfcId',
                            hsbentity instanceof HsbElement
                                ? 'Property.ElementInfo'
                                : 'Property.EntityInfo'
                        )
                    );
                } else if (entity.geomhandle != null) {
                    hsbentity.geomhandle = entity.geomhandle;
                    hsbentity.addProperty(
                        new Property(
                            'geomhandle',
                            hsbentity instanceof HsbElement ? 'Element info' : 'Entity info',
                            hsbentity.geomhandle,
                            true,
                            'Property.Geomhandle',
                            hsbentity instanceof HsbElement
                                ? 'Property.ElementInfo'
                                : 'Property.EntityInfo'
                        )
                    );
                } else if (hsbentity.solidheight != null) {
                    hsbentity.addProperty(
                        new Property(
                            'solidheight',
                            'Geometry',
                            hsbentity.solidheight * unitFactor,
                            true,
                            'Property.Height',
                            'Property.Geometry'
                        )
                    );
                } else if (hsbentity.solidwidth != null) {
                    hsbentity.addProperty(
                        new Property(
                            'solidwidth',
                            'Geometry',
                            hsbentity.solidwidth * unitFactor,
                            true,
                            'Property.Width',
                            'Property.Geometry'
                        )
                    );
                } else if (hsbentity.solidlength != null) {
                    hsbentity.addProperty(
                        new Property(
                            'solidlength',
                            'Geometry',
                            hsbentity.solidlength * unitFactor,
                            true,
                            'Property.Length',
                            'Property.Geometry'
                        )
                    );
                } else if (hsbentity.volume != null) {
                    hsbentity.addProperty(
                        new Property(
                            'volume',
                            'Geometry',
                            hsbentity.volume * Math.pow(unitFactor, 3),
                            true,
                            'Property.Volume',
                            'Property.Geometry'
                        )
                    );
                } else if (entity.zoneindex != null) {
                    hsbentity.zone = entity.zoneindex;
                    hsbentity.addProperty(
                        new Property(
                            'zone',
                            hsbentity instanceof HsbElement ? 'Element info' : 'Entity info',
                            entity.zoneindex,
                            true,
                            'Property.Zone',
                            hsbentity instanceof HsbElement
                                ? 'Property.ElementInfo'
                                : 'Property.EntityInfo'
                        )
                    );
                } else if (entity.group != null) {
                    var newgroup = hsbModel.findGroupbyHandle(entity.group.handle);
                    if (newgroup != null) {
                        hsbentity.groups.push(newgroup);

                        if (hsbentity instanceof HsbElement) {
                            hsbentity.group = newgroup;
                        }
                    }
                } else if (entity.elementfloorgroup != null) {
                    var group = hsbModel.findGroupbyHandle(entity.elementfloorgroup);
                    if (group != null) {
                        newgroup = new Group(0);

                        newgroup.house = group.house;
                        newgroup.floor = group.floor;

                        var foundgroup = hsbModel.findGroup(newgroup);

                        if (foundgroup) {
                            hsbentity.group = foundgroup;
                        } else {
                            hsbentity.group = group;
                        }
                    }
                } else if (entity.ptorg != null) {
                    hsbentity.coordSys.ptOrg = new THREE.Vector3(
                        entity.ptorg.x,
                        entity.ptorg.y,
                        entity.ptorg.z
                    ).multiplyScalar(unitFactor);
                } else if (entity.vecx != null) {
                    hsbentity.coordSys.vecX = new THREE.Vector3(
                        entity.vecx.x,
                        entity.vecx.y,
                        entity.vecx.z
                    );
                } else if (entity.vecy != null) {
                    hsbentity.coordSys.vecY = new THREE.Vector3(
                        entity.vecy.x,
                        entity.vecy.y,
                        entity.vecy.z
                    );
                } else if (entity.vecz != null) {
                    hsbentity.coordSys.vecZ = new THREE.Vector3(
                        entity.vecz.x,
                        entity.vecz.y,
                        entity.vecz.z
                    );
                } else if (entity.color !== undefined) {
                    var color = '255,0,0';

                    if (entity.color < 0) {
                        let toBytesInt32 = function (num: number) {
                            let arr = new Uint8Array([
                                (num & 0xff000000) >> 24,
                                (num & 0x00ff0000) >> 16,
                                (num & 0x0000ff00) >> 8,
                                num & 0x000000ff,
                            ]);
                            return arr;
                        };

                        var colorarray = toBytesInt32(entity.color);
                        color = colorarray[1] + ',' + colorarray[2] + ',' + colorarray[3];
                    } else {
                        if (entity.color > 255 || entity.color === 0) {
                            entity.color = 9;
                        }
                        color = _self.ACArgbValues[entity.color];
                    }
                    hsbentity.color = _self.rgb2hex(color);
                } else if (entity.colorrgb != null) {
                    hsbentity.color = entity.colorrgb;
                } else if (entity.transparency != null) {
                    hsbentity.transparency = entity.transparency;
                } else if (entity.element != null) {
                    hsbentity.elementhandle = entity.element;
                } else if (entity.nm && entity.nm.toLowerCase() === 'simplebody') {
                    _self.setBodyData(hsbentity, entity, unitFactor);
                } else if (
                    entity.nm &&
                    (entity.nm.toLowerCase() === 'propset[]' ||
                        entity.nm.toLowerCase() === 'mapx[]' ||
                        entity.nm.toLowerCase() === 'propstring[]') &&
                    entity.arr
                ) {
                    // Only expose propset when the propset is not a mapx or when it has a revitID.
                    var expose: boolean =
                        entity.nm.toLowerCase() !== 'mapx[]' ||
                        entity.arr
                            .map((prop: { arr: { nm: any }[] }) => prop.arr[0].nm)
                            .includes('REVITID');

                    entity.arr.forEach((propsetarr: { arr: any[] }) => {
                        var propertycat: string;
                        for (var z = 0; z < 2; z++) {
                            // eslint-disable-next-line no-loop-func
                            propsetarr.arr.forEach(
                                (propset: {
                                    [x: string]: any;
                                    ty: string;
                                    nm: any;
                                    hasOwnProperty: (arg0: string) => any;
                                }) => {
                                    if (z === 0) {
                                        if (propset.ty === 'key') {
                                            propertycat = propset.nm;
                                            // Dont expsoe host properties
                                            expose = expose && propertycat.toLowerCase() !== 'host';
                                        }
                                    } else {
                                        if (propset.ty !== 'key') {
                                            for (var prop in propset) {
                                                if (propset.hasOwnProperty(prop)) {
                                                    if (
                                                        prop !== 'ty' &&
                                                        prop !== 'ut' &&
                                                        prop !== 'trm'
                                                    ) {
                                                        var val = propset[prop];

                                                        if (
                                                            val.x != null &&
                                                            val.y != null &&
                                                            val.z != null
                                                        ) {
                                                            val = new THREE.Vector3(
                                                                val.x,
                                                                val.y,
                                                                val.z
                                                            );
                                                        }

                                                        hsbentity.addProperty(
                                                            new Property(
                                                                prop,
                                                                propertycat,
                                                                val,
                                                                expose
                                                            )
                                                        );
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            );
                        }
                    });
                }
            });
        }
    }

    loadIFCEntities(
        hsbMap: { nm: string; arr: any[] },
        currentgroupnode: Node | null,
        hsbModel: HsbModel,
        hsbProject: {
            nodeModel: {
                createNode: (arg0: any, arg1: any) => any;
                addChildNode: (arg0: any, arg1: any) => void;
                findGroupChildNode: (arg0: any, arg1: any) => any;
            };
            EntityTable: { [x: string]: { [x: string]: NonHsb } };
        }
    ) {
        var _self = this;
        var hsbentity: any;
        var entityname = hsbMap.nm ? hsbMap.nm.toLowerCase() : null;

        if (entityname === 'ifcentity') {
            var ifcent: any = {};

            hsbMap.arr.forEach(
                (ifcentity: { name: any; ty: string; nm: string; ifcid: any; arr: any }) => {
                    if (ifcentity.name) {
                        ifcent.name = ifcentity.name;
                    } else if (ifcentity.ty === 'key') {
                        ifcent.objecttype = ifcentity.nm.toLowerCase();
                    } else if (ifcentity.ifcid) {
                        ifcent.ifcid = ifcentity.ifcid;
                    } else if (
                        ifcentity.nm &&
                        ifcentity.nm.toLowerCase() === 'propset[]' &&
                        ifcentity.arr
                    ) {
                    }
                }
            );

            // Create node leaf node if we have further ifcentity node.
            var ifcelementchilds = hsbMap.arr.filter(function (e: { nm: string }) {
                return e.nm && e.nm.toLowerCase() === 'ifcentity';
            });

            // eslint-disable-next-line no-mixed-operators
            if (
                !_self._treeconfig ||
                (_self._treeconfig &&
                    _self._treeconfig.ifcobjectfilter &&
                    _self._treeconfig.ifcobjectfilter.filterset[
                        _self._treeconfig.ifcobjectfilter.active
                    ] &&
                    // eslint-disable-next-line no-mixed-operators
                    _self._treeconfig.ifcobjectfilter.filterset[
                        _self._treeconfig.ifcobjectfilter.active
                    ][IFCObjectType.IfcAllObjects]) ||
                _self._treeconfig.ifcobjectfilter.filterset[
                    _self._treeconfig.ifcobjectfilter.active
                ][ifcent.objecttype]
            ) {
                if (ifcelementchilds.length > 0) {
                    var groupnode = hsbProject.nodeModel.createNode(ifcent.name, currentgroupnode);
                    groupnode.pathid = newGuid().toLowerCase();
                    groupnode.metadata.data = {
                        nodetype: NodeType.Geom,
                        itemtype: NodeType.Geom,
                        refitemtype: RefItemType.Parent,
                    };
                    groupnode.hsbmodel = hsbModel;
                    hsbProject.nodeModel.addChildNode(currentgroupnode, groupnode);
                    currentgroupnode = groupnode;
                }
            }

            hsbentity = new NonHsb(0);
            _self.setEntityData(hsbentity, hsbMap, hsbModel);

            if (!hsbentity.handle) {
            }

            if (hsbentity && hsbentity.handle !== '0') {
                hsbentity.hsbmodel = hsbModel;
                hsbentity.objecttype = ifcent.objecttype;

                // Add to fast lookup table.
                if (!hsbProject.EntityTable[hsbModel.modelId]) {
                    hsbProject.EntityTable[hsbModel.modelId] = {};
                }

                hsbProject.EntityTable[hsbModel.modelId][hsbentity.handle] = hsbentity;
                hsbModel.Entities.push(hsbentity);

                if (
                    !_self._treeconfig ||
                    (_self._treeconfig &&
                        _self._treeconfig.ifcobjectfilter &&
                        _self._treeconfig.ifcobjectfilter.filterset[
                            _self._treeconfig.ifcobjectfilter.active
                        ] &&
                        _self._treeconfig.ifcobjectfilter.filterset[
                            _self._treeconfig.ifcobjectfilter.active
                        ][IFCObjectType.IfcAllObjects]) ||
                    _self._treeconfig.ifcobjectfilter.filterset[
                        _self._treeconfig.ifcobjectfilter.active
                    ][ifcent.objecttype]
                ) {
                    // If this is root node - don't add another node here - we're already added one.
                    if (ifcelementchilds.length > 0 && currentgroupnode && hsbentity) {
                        currentgroupnode.entity = hsbentity;
                        currentgroupnode.entity!.node = currentgroupnode;
                        currentgroupnode.pathid = hsbentity.handle.toLowerCase();
                    } else {
                        var elementsubnode = hsbProject.nodeModel.findGroupChildNode(
                            currentgroupnode,
                            hsbentity.objecttype
                        );
                        if (!elementsubnode) {
                            elementsubnode = hsbProject.nodeModel.createNode(
                                hsbentity.objecttype,
                                currentgroupnode
                            );
                            elementsubnode.subgroup = hsbentity.objecttype;
                            elementsubnode.hsbmodel = hsbModel;
                            hsbProject.nodeModel.addChildNode(currentgroupnode, elementsubnode);
                        }

                        var groupnode = hsbProject.nodeModel.createNode(
                            ifcent.name,
                            elementsubnode
                        );
                        groupnode.pathid = hsbentity.handle.toLowerCase();
                        groupnode.metadata.data = {
                            nodetype: NodeType.Geom,
                            itemtype: NodeType.Geom,
                            refitemtype: RefItemType.Parent,
                        };
                        groupnode.hsbmodel = hsbModel;
                        groupnode.entity = hsbentity;
                        groupnode.entity.node = groupnode;

                        hsbProject.nodeModel.addChildNode(elementsubnode, groupnode);
                    }
                }
            }

            // Got the base info - so add the new group
            hsbMap.arr.forEach((ifcentity: any) => {
                var entryname = ifcentity.nm ? ifcentity.nm.toLowerCase() : null;

                if (ifcentity.name) {
                    ifcent.name = ifcentity.name;
                } else if (ifcentity.ty === 'key') {
                    ifcent.objecttype = ifcentity.nm;
                } else if (entryname === 'entity') {
                    var hsbentity = new NonHsb(0);

                    _self.setEntityData(hsbentity, ifcentity, hsbModel);

                    if (hsbentity && hsbentity.handle !== '0') {
                        hsbentity.hsbmodel = hsbModel;
                        hsbentity.objecttype = ifcent.objecttype;
                        hsbModel.Entities.push(hsbentity);

                        // If this is root node - don't add another node here - we're already added one.
                        if (ifcelementchilds.length > 0 && currentgroupnode && hsbentity) {
                            currentgroupnode.entity = hsbentity;
                            currentgroupnode.entity.node = currentgroupnode;
                            currentgroupnode.pathid = hsbentity.handle.toLowerCase();
                        } else {
                            var groupnode = hsbProject.nodeModel.createNode(
                                ifcent.name,
                                currentgroupnode
                            );
                            groupnode.pathid = hsbentity.handle.toLowerCase();
                            groupnode.metadata.data = {
                                nodetype: NodeType.Geom,
                                itemtype: NodeType.Geom,
                                refitemtype: RefItemType.Parent,
                            };
                            groupnode.hsbmodel = hsbModel;
                            groupnode.entity = hsbentity;
                            groupnode.entity.node = groupnode;

                            hsbProject.nodeModel.addChildNode(currentgroupnode, groupnode);
                        }

                        // Add to fast lookup table.
                        if (!hsbProject.EntityTable[hsbModel.modelId]) {
                            hsbProject.EntityTable[hsbModel.modelId] = {};
                        }

                        hsbProject.EntityTable[hsbModel.modelId][hsbentity.handle] = hsbentity;
                    }
                } else if (entryname === 'ifcentity') {
                    _self.loadIFCEntities(ifcentity, currentgroupnode, hsbModel, hsbProject);
                }
            });
        }
    }
    addEntityTypeData(
        hsbProject: HsbProject,
        hsbModel: HsbModel,
        hsbMap: { nm: string; arr: any[] }
    ) {
        let _self = this;
        let hsbentity: any;
        const unitFactor = hsbModel.Units.factor;
        var entityname = hsbMap.nm ? hsbMap.nm.toLowerCase() : null;

        if (entityname === 'beam' || entityname === 'sheet' || entityname === 'sip') {
            if (entityname === 'sheet') {
                hsbentity = new Sheet('0', '0', 0);
            } else if (entityname === 'sip') {
                hsbentity = new Sip('0', '0', 0);
            } else {
                hsbentity = new Beam('0', '0', 0);
            }

            hsbMap.arr.forEach(
                (elementbeam: {
                    nm: string;
                    arr: any[];
                    woodgraindirection: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    };
                    style: any;
                }) => {
                    if (elementbeam.nm && elementbeam.nm.toLowerCase() === 'genbeam') {
                        elementbeam.arr.forEach(
                            (genbeam: {
                                posnum: null;
                                name: null;
                                grade: null;
                                information: null;
                                label: null;
                                material: null;
                                sublabel: null;
                                sublabel2: null;
                                beamcode: null;
                                hsbid: null;
                                type: string | any[] | null;
                                solidheight: number | null;
                                solidwidth: number | null;
                                solidlength: number | null;
                                volume: number | null;
                            }) => {
                                _self.setEntityData(hsbentity, genbeam, hsbModel);

                                if (genbeam.posnum != null) {
                                    hsbentity.posnum = genbeam.posnum;
                                    hsbentity.addProperty(
                                        new Property(
                                            'posnum',
                                            'Data',
                                            genbeam.posnum,
                                            true,
                                            'Property.Posnum',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.name != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'name',
                                            'Data',
                                            genbeam.name,
                                            true,
                                            'Property.Name',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.grade != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'grade',
                                            'Data',
                                            genbeam.grade,
                                            true,
                                            'Property.Grade',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.information != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'information',
                                            'Data',
                                            genbeam.information,
                                            true,
                                            'Property.Information',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.label != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'label',
                                            'Data',
                                            genbeam.label,
                                            true,
                                            'Property.Label',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.material != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'material',
                                            'Data',
                                            genbeam.material,
                                            true,
                                            'Property.Material',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.sublabel != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'sublabel',
                                            'Data',
                                            genbeam.sublabel,
                                            true,
                                            'Property.Sublabel',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.sublabel2 != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'sublabel2',
                                            'Data',
                                            genbeam.sublabel2,
                                            true,
                                            'Property.Sublabel2',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.beamcode != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'beamcode',
                                            'Data',
                                            genbeam.beamcode,
                                            false,
                                            'Property.Beamcode',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.hsbid != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'hsbid',
                                            'Data',
                                            genbeam.hsbid,
                                            true,
                                            'Property.Hsbid',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.type != null) {
                                    var val = genbeam.type.slice(2);
                                    hsbentity.addProperty(
                                        new Property(
                                            'type',
                                            'Data',
                                            val,
                                            true,
                                            'Property.Type',
                                            'Property.Data'
                                        )
                                    );
                                } else if (genbeam.solidheight != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'solidheight',
                                            'Geometry',
                                            genbeam.solidheight * unitFactor,
                                            true,
                                            'Property.Height',
                                            'Property.Geometry'
                                        )
                                    );
                                } else if (genbeam.solidwidth != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'solidwidth',
                                            'Geometry',
                                            genbeam.solidwidth * unitFactor,
                                            true,
                                            'Property.Width',
                                            'Property.Geometry'
                                        )
                                    );
                                } else if (genbeam.solidlength != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'solidlength',
                                            'Geometry',
                                            genbeam.solidlength * unitFactor,
                                            true,
                                            'Property.Length',
                                            'Property.Geometry'
                                        )
                                    );
                                } else if (genbeam.volume != null) {
                                    hsbentity.addProperty(
                                        new Property(
                                            'volume',
                                            'Geometry',
                                            genbeam.volume * Math.pow(unitFactor, 3),
                                            true,
                                            'Property.Volume',
                                            'Property.Geometry'
                                        )
                                    );
                                }
                            }
                        );

                        // Check if coordsys has been set.
                        elementbeam.arr.forEach(
                            (genbeam: {
                                ptcen: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                vecx: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                vecy: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                vecz: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                            }) => {
                                if (genbeam.ptcen != null) {
                                    hsbentity.coordSys.ptOrg = new Vector3(
                                        genbeam.ptcen.x,
                                        genbeam.ptcen.y,
                                        genbeam.ptcen.z
                                    ).multiplyScalar(unitFactor);
                                } else if (genbeam.vecx != null) {
                                    hsbentity.coordSys.vecX = new Vector3(
                                        genbeam.vecx.x,
                                        genbeam.vecx.y,
                                        genbeam.vecx.z
                                    );
                                } else if (genbeam.vecy != null) {
                                    hsbentity.coordSys.vecY = new Vector3(
                                        genbeam.vecy.x,
                                        genbeam.vecy.y,
                                        genbeam.vecy.z
                                    );
                                } else if (genbeam.vecz != null) {
                                    hsbentity.coordSys.vecZ = new Vector3(
                                        genbeam.vecz.x,
                                        genbeam.vecz.y,
                                        genbeam.vecz.z
                                    );
                                }
                            }
                        );
                    } else if (elementbeam.woodgraindirection) {
                        let grainDirection = new Vector3(
                            elementbeam.woodgraindirection.x,
                            elementbeam.woodgraindirection.y,
                            elementbeam.woodgraindirection.z
                        );
                        if (!grainDirection.equals(new Vector3())) {
                            hsbentity.woodgraindirection = grainDirection;
                        }
                    } else if (elementbeam.style) {
                        hsbentity.addProperty(
                            new Property(
                                'style',
                                'Data',
                                elementbeam.style,
                                true,
                                'Property.Style',
                                'Property.Data'
                            )
                        );
                    }
                }
            );
        } else if (entityname == 'openingsf') {
            hsbentity = new Opening('0', '0', 0);

            hsbMap.arr.forEach((openingent: any) => {
                if (openingent.nm?.toLowerCase() == 'opening') {
                    _self.setEntityData(hsbentity, openingent, hsbModel);
                    _self.setOpeningEntityData(hsbentity, openingent, hsbModel);
                }
            });
        } else if (entityname == 'opening') {
            hsbentity = new Opening('0', '0', 0);
            _self.setEntityData(hsbentity, hsbMap, hsbModel);
            _self.setOpeningEntityData(hsbentity, hsbMap, hsbModel);
        } else if (entityname === 'massgroup' || entityname === 'masselement') {
            hsbentity =
                entityname === 'massgroup'
                    ? new MassGroup('0', ' 0', 0)
                    : new MassElement('0', '0', 0);

            hsbMap.arr.forEach((massgroup: { posnum: null }) => {
                _self.setEntityData(hsbentity, massgroup, hsbModel);

                if (massgroup.posnum != null) {
                    hsbentity.posnum = massgroup.posnum;
                    hsbentity.addProperty(
                        new Property(
                            'posnum',
                            'Data',
                            massgroup.posnum,
                            true,
                            'Property.Posnum',
                            'Property.Data'
                        )
                    );
                }
            });

            hsbMap.arr.forEach(
                (massgroup: {
                    ptorg: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecx: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecy: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    vecz: {
                        x: number | undefined;
                        y: number | undefined;
                        z: number | undefined;
                    } | null;
                    posnum: null;
                }) => {
                    if (!hsbentity.coordSys.ptOrg && massgroup.ptorg != null) {
                        hsbentity.coordSys.ptOrg = new THREE.Vector3(
                            massgroup.ptorg.x,
                            massgroup.ptorg.y,
                            massgroup.ptorg.z
                        ).multiplyScalar(unitFactor);
                    } else if (!hsbentity.coordSys.vecX && massgroup.vecx != null) {
                        hsbentity.coordSys.vecX = new THREE.Vector3(
                            massgroup.vecx.x,
                            massgroup.vecx.y,
                            massgroup.vecx.z
                        );
                    } else if (!hsbentity.coordSys.vecY && massgroup.vecy != null) {
                        hsbentity.coordSys.vecY = new THREE.Vector3(
                            massgroup.vecy.x,
                            massgroup.vecy.y,
                            massgroup.vecy.z
                        );
                    } else if (!hsbentity.coordSys.vecZ && massgroup.vecz != null) {
                        hsbentity.coordSys.vecZ = new THREE.Vector3(
                            massgroup.vecz.x,
                            massgroup.vecz.y,
                            massgroup.vecz.z
                        );
                    } else if (massgroup.posnum != null) {
                        hsbentity.posnum = massgroup.posnum;
                        hsbentity.addProperty(
                            new Property(
                                'posnum',
                                'Data',
                                massgroup.posnum,
                                true,
                                'Property.Posnum',
                                'Property.Data'
                            )
                        );
                    }
                }
            );
        } else if (entityname === 'metalpartcollectionent') {
            hsbentity = new MetalPartCollectionEnt(0, '0', 0);

            hsbMap.arr.forEach((collectionent: any) => {
                if (collectionent.nm && collectionent.nm.toLowerCase() === 'collectionentity') {
                    _self.setCollectionEntityData(hsbentity, collectionent, hsbModel);
                }
            });
        } else if (entityname === 'blockreference') {
            hsbentity = new BlockReference(0, '0', 0);
            _self.setBlockReferenceEntityData(hsbentity, hsbMap, hsbModel);
        } else if (entityname === 'collectionentity') {
            hsbentity = new CollectionEntity(0, COLLECTIONENTITY, ObjectType.HsbCollectionEntity);
            _self.setCollectionEntityData(hsbentity, hsbMap, hsbModel);
        } else if (entityname === 'trussentity') {
            hsbentity = new TrussEntity(0);

            hsbMap.arr.forEach((collectionData: any) => {
                if (collectionData.nm === 'CollectionEntity') {
                    _self.setCollectionEntityData(hsbentity, collectionData, hsbModel);
                }
            });
        } else if (entityname === 'tslinst' || entityname === 'fastenerassemblyent') {
            if (entityname === 'tslinst') {
                hsbentity = new TslInst(0, '0', 0);

                hsbMap.arr.forEach((tslinst: { nm: string; arr: any[] }) => {
                    if (tslinst.nm && tslinst.nm.toLowerCase() === 'toolent') {
                        tslinst.arr.forEach((toolent: { nm: string; arr: any[] }) => {
                            if (toolent.nm && toolent.nm.toLowerCase() === 'entity') {
                                toolent.arr.forEach((ent: { nm: string; arr: any[] }) => {
                                    if (ent.nm && ent.nm.toLowerCase() === 'mapx[]') {
                                        ent.arr.forEach((mapx: { arr: any[] }) => {
                                            mapx.arr.forEach((mapxvals: { nm: string }) => {
                                                if (mapxvals.nm) {
                                                }

                                                if (
                                                    mapxvals.nm &&
                                                    mapxvals.nm.toLowerCase() ===
                                                        'hsb_nestingparent'
                                                ) {
                                                    hsbentity = new Nesting(0);
                                                } else if (
                                                    mapxvals.nm &&
                                                    mapxvals.nm.toLowerCase() ===
                                                        'hsb_stackingparent'
                                                ) {
                                                    hsbentity = new Stack(0);
                                                } else if (
                                                    mapxvals.nm &&
                                                    mapxvals.nm.toLowerCase() === 'hsb_truckparent'
                                                ) {
                                                    hsbentity = new Stack(0);
                                                }
                                            });
                                        });
                                    }
                                });
                            }
                        });
                    }
                });
            } else if (entityname === 'fastenerassemblyent') {
                hsbentity = new FastenerAssemblyEnt(0, '0', 0);
            }
            hsbMap.arr.forEach((tslinst: { nm: string; arr: any[]; scriptname: any }) => {
                if (tslinst.nm && tslinst.nm.toLowerCase() === 'toolent') {
                    tslinst.arr.forEach((toolent: { posnum: null; nm: string; arr: any[] }) => {
                        _self.setEntityData(hsbentity, toolent, hsbModel);

                        if (toolent.posnum != null) {
                            hsbentity.posnum = toolent.posnum;
                            hsbentity.addProperty(
                                new Property(
                                    'posnum',
                                    'Data',
                                    toolent.posnum,
                                    true,
                                    'Property.Posnum',
                                    'Property.Data'
                                )
                            );
                        } else if (toolent.nm && toolent.nm.toLowerCase() === 'handle[]') {
                            hsbentity.entities = [];
                        }
                    });
                } else if (tslinst.scriptname) {
                    hsbentity.scriptname = tslinst.scriptname;

                    if (hsbentity.scriptname) {
                        hsbentity.addProperty(
                            new Property(
                                'scriptname',
                                'Data',
                                hsbentity.scriptname,
                                true,
                                'Property.Scriptname',
                                'Property.Data'
                            )
                        );
                    }
                } else if (tslinst.nm && tslinst.arr && tslinst.nm.toLowerCase() === 'map') {
                    tslinst.arr.forEach(
                        (propset: { ty: string; hasOwnProperty: (arg0: string) => any }) => {
                            if (propset.ty !== 'key') {
                                for (var prop in propset) {
                                    if (propset.hasOwnProperty(prop)) {
                                        if (prop !== 'ty' && prop !== 'ut') {
                                            //hsbentity.addProperty(new Property(prop, "inventory", val));
                                        }
                                    }
                                }
                            }
                        }
                    );
                } else if (
                    tslinst.nm &&
                    tslinst.arr &&
                    tslinst.nm.toLowerCase() === 'propstring[]'
                ) {
                    tslinst.arr.forEach((prop: { arr: any[] }) => {
                        var name = prop.arr.filter(function (e: { strname: any }) {
                            return e.strname;
                        })[0];
                        var value = prop.arr.filter(function (e: { strvalue: any }) {
                            return e.strvalue;
                        })[0];

                        if (name && value) {
                            hsbentity.addProperty(
                                new Property(
                                    name[Object.keys(name)[0]],
                                    'Data',
                                    value[Object.keys(value)[0]],
                                    false
                                )
                            );
                        }
                    });
                } else if (tslinst.nm && tslinst.arr && tslinst.nm.toLowerCase() === 'propint[]') {
                    tslinst.arr.forEach((prop: { arr: any[] }) => {
                        var name = prop.arr.filter(function (e: { strname: any }) {
                            return e.strname;
                        })[0];
                        var value = prop.arr.filter(function (e: { ivalue: any }) {
                            return e.ivalue;
                        })[0];

                        if (name && value) {
                            hsbentity.addProperty(
                                new Property(
                                    name[Object.keys(name)[0]],
                                    'Data',
                                    value[Object.keys(value)[0]],
                                    false
                                )
                            );
                        }
                    });
                } else if (
                    tslinst.nm &&
                    tslinst.arr &&
                    tslinst.nm.toLowerCase() === 'propdouble[]'
                ) {
                    tslinst.arr.forEach((prop: { arr: any[] }) => {
                        var name = prop.arr.filter(function (e: { strname: any }) {
                            return e.strname;
                        })[0];
                        var value = prop.arr.filter(function (e: { dvalue: any }) {
                            return e.dvalue;
                        })[0];

                        if (name && value) {
                            hsbentity.addProperty(
                                new Property(
                                    name[Object.keys(name)[0]],
                                    'Data',
                                    value[Object.keys(value)[0]],
                                    false
                                )
                            );
                        }
                    });
                } else if (tslinst.nm && tslinst.arr && tslinst.nm.toLowerCase() === 'display[]') {
                    tslinst.arr.forEach((display: { arr: any[] }) => {
                        if (!display.arr) return;
                        display.arr.forEach((displayDim: { nm: string; arr: any[] }) => {
                            if (displayDim.nm.toLowerCase() === 'drawdim') {
                                let handle = hsbentity._handle;
                                hsbentity = new Dimension('0');
                                hsbentity.handle = handle;
                                displayDim.arr.forEach(
                                    (drawDim: {
                                        color: null;
                                        ptline: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        vecxline: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        vecyline: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        vecread: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        idisplaydel: null;
                                        idisplaycumm: null;
                                        bdeltaontop: null;
                                        bchaindim: null;
                                        btextheight: null;
                                        dtextheight: any;
                                        dlinearscale: null;
                                        layer: null;
                                        transparentpct: number | null;
                                        dimstyle: null;
                                        nm: string;
                                        arr: any[];
                                    }) => {
                                        if (drawDim.color != null) {
                                            hsbentity.Color = drawDim.color;
                                        } else if (drawDim.ptline != null) {
                                            hsbentity._coordSys.ptOrg = new THREE.Vector3(
                                                drawDim.ptline.x,
                                                drawDim.ptline.y,
                                                drawDim.ptline.z
                                            ).multiplyScalar(unitFactor);
                                        } else if (drawDim.vecxline != null) {
                                            hsbentity.XDirection = new THREE.Vector3(
                                                drawDim.vecxline.x,
                                                drawDim.vecxline.y,
                                                drawDim.vecxline.z
                                            );
                                        } else if (drawDim.vecyline != null) {
                                            hsbentity.YDirection = new THREE.Vector3(
                                                drawDim.vecyline.x,
                                                drawDim.vecyline.y,
                                                drawDim.vecyline.z
                                            );
                                        } else if (drawDim.vecread != null) {
                                            hsbentity.Visible = new THREE.Vector3(
                                                drawDim.vecread.x,
                                                drawDim.vecread.y,
                                                drawDim.vecread.z
                                            );
                                        } else if (drawDim.idisplaydel != null) {
                                            hsbentity.DisplayDel = drawDim.idisplaydel;
                                        } else if (drawDim.idisplaycumm != null) {
                                            hsbentity.DisplayCummulative = drawDim.idisplaycumm;
                                        } else if (drawDim.bdeltaontop != null) {
                                            hsbentity.DeltaOnTop = !!drawDim.bdeltaontop;
                                        } else if (drawDim.bchaindim != null) {
                                            hsbentity.ChainDim = !!drawDim.bchaindim;
                                        } else if (drawDim.btextheight != null) {
                                            hsbentity.TextHeight = drawDim.dtextheight;
                                        } else if (drawDim.dlinearscale != null) {
                                            hsbentity.LinScale = drawDim.dlinearscale;
                                        } else if (drawDim.layer != null) {
                                            hsbentity.Layer = drawDim.layer;
                                        } else if (drawDim.transparentpct != null) {
                                            hsbentity.Transparency =
                                                1 - drawDim.transparentpct / 100;
                                        } else if (drawDim.dimstyle != null) {
                                            hsbentity.Style = drawDim.dimstyle;
                                        } else if (
                                            drawDim.nm &&
                                            drawDim.nm.toLowerCase() === 'node[]'
                                        ) {
                                            let nodes: any[] = [];
                                            drawDim.arr.forEach((node: { arr: any[] }) => {
                                                let nodeData: any = {
                                                    pt: null,
                                                    bisreference: false,
                                                };
                                                nodes.push(nodeData);
                                                node.arr.forEach(
                                                    (nodeVal: {
                                                        pt: {
                                                            x: number | undefined;
                                                            y: number | undefined;
                                                            z: number | undefined;
                                                        } | null;
                                                        bisreference: null;
                                                    }) => {
                                                        if (nodeVal.pt != null) {
                                                            nodeData.pt = new THREE.Vector3(
                                                                nodeVal.pt.x,
                                                                nodeVal.pt.y,
                                                                nodeVal.pt.z
                                                            );
                                                        } else if (nodeVal.bisreference != null) {
                                                            nodeData.bisreference =
                                                                !!nodeVal.bisreference;
                                                        }
                                                    }
                                                );
                                            });
                                            hsbentity.Nodes = nodes;
                                        }
                                    }
                                );
                            }
                        });
                    });
                }
            });
        }
        return hsbentity;
    }

    loadEntities(model: { arr: any[] }, hsbModel: HsbModel, hsbProject: HsbProject) {
        const unitFactor = hsbModel.Units.factor;
        var _self = this;
        // Create all Entities, Elements here...
        model.arr.forEach((hsbMap: { nm: string; arr: any[] }) => {
            var hsbentity: any;
            var entityname = hsbMap.nm ? hsbMap.nm.toLowerCase() : null;
            if (entityname === 'entity') {
                var found = hsbMap.arr.filter(function (e: { typename: any }) {
                    return e.typename;
                })[0];
                if (found && found.typename.toLowerCase() === 'acdb3dsolid') {
                    hsbentity = new GenericModel(0, '0', 0);
                    _self.setEntityData(hsbentity, hsbMap, hsbModel);
                }
            } else if (entityname === 'grid') {
                hsbentity = new Grid('0');
                _self.setGridEntityData(hsbentity as Grid, hsbMap, hsbModel);
            } else if (
                entityname &&
                ['elementwallsf', 'elementlog', 'elementwallsip'].includes(entityname)
            ) {
                hsbentity =
                    entityname === 'elementwallsip'
                        ? new ElementWallSIP(0, 0)
                        : new ElementWall(0, 0);

                hsbMap.arr.forEach(
                    (elementwallsfval: { loadBearing: null; nm: string; arr: any[] }) => {
                        if (elementwallsfval.loadBearing != null) {
                            hsbentity.addProperty(
                                new Property(
                                    'loadBearing',
                                    'Wall info',
                                    elementwallsfval.loadBearing,
                                    true,
                                    'Property.LoadBearing',
                                    'Property.WallInfo'
                                )
                            );
                        }

                        if (
                            elementwallsfval.nm &&
                            elementwallsfval.nm.toLowerCase() === 'elementwall'
                        ) {
                            elementwallsfval.arr.forEach(
                                (elementwall: {
                                    exposed: null;
                                    roofcode: null;
                                    ptarrow: {
                                        x: number | undefined;
                                        y: number | undefined;
                                        z: number | undefined;
                                    } | null;
                                    nm: string;
                                    arr: any[];
                                }) => {
                                    if (elementwall.exposed != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'exposed',
                                                'Wall info',
                                                elementwall.exposed,
                                                true,
                                                'Property.Exposed',
                                                'Property.WallInfo'
                                            )
                                        );
                                    } else if (elementwall.roofcode != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'roofcode',
                                                'Wall info',
                                                elementwall.roofcode,
                                                true,
                                                'Property.Roofcode',
                                                'Property.WallInfo'
                                            )
                                        );
                                    } else if (elementwall.ptarrow != null) {
                                        hsbentity.ptarrow = new THREE.Vector3(
                                            elementwall.ptarrow.x,
                                            elementwall.ptarrow.y,
                                            elementwall.ptarrow.z
                                        ).multiplyScalar(unitFactor);
                                    }
                                    let wallentity;

                                    if (
                                        elementwall.nm &&
                                        elementwall.nm.toLowerCase() === 'element'
                                    ) {
                                        hsbentity.elementCoordSys = new CoordSys();

                                        elementwall.arr.forEach(
                                            (element: {
                                                nm: string;
                                                arr: any;
                                                ptorg: {
                                                    x: number | undefined;
                                                    y: number | undefined;
                                                    z: number | undefined;
                                                } | null;
                                                vecx: {
                                                    x: number | undefined;
                                                    y: number | undefined;
                                                    z: number | undefined;
                                                } | null;
                                                vecy: {
                                                    x: number | undefined;
                                                    y: number | undefined;
                                                    z: number | undefined;
                                                } | null;
                                                vecz: {
                                                    x: number | undefined;
                                                    y: number | undefined;
                                                    z: number | undefined;
                                                } | null;
                                                number: null;
                                                code: null;
                                                information: null;
                                                subtype: null;
                                                quantity: null;
                                            }) => {
                                                if (
                                                    element.nm &&
                                                    element.nm.toLowerCase() === 'wall'
                                                ) {
                                                    // Process after we've loaded all other values.
                                                    wallentity = element.arr;
                                                }

                                                if (element.ptorg != null) {
                                                    hsbentity.elementCoordSys.ptOrg =
                                                        new THREE.Vector3(
                                                            element.ptorg.x,
                                                            element.ptorg.y,
                                                            element.ptorg.z
                                                        ).multiplyScalar(unitFactor);
                                                } else if (element.vecx != null) {
                                                    hsbentity.elementCoordSys.vecX =
                                                        new THREE.Vector3(
                                                            element.vecx.x,
                                                            element.vecx.y,
                                                            element.vecx.z
                                                        );
                                                }
                                                if (element.vecy != null) {
                                                    hsbentity.elementCoordSys.vecY =
                                                        new THREE.Vector3(
                                                            element.vecy.x,
                                                            element.vecy.y,
                                                            element.vecy.z
                                                        );
                                                }
                                                if (element.vecz != null) {
                                                    hsbentity.elementCoordSys.vecZ =
                                                        new THREE.Vector3(
                                                            element.vecz.x,
                                                            element.vecz.y,
                                                            element.vecz.z
                                                        );
                                                } else if (element.number != null) {
                                                    hsbentity.addProperty(
                                                        new Property(
                                                            'number',
                                                            'Element info',
                                                            element.number,
                                                            true,
                                                            'Property.Number',
                                                            'Property.ElementInfo'
                                                        )
                                                    );
                                                    hsbentity.number = element.number;
                                                } else if (element.code != null) {
                                                    hsbentity.addProperty(
                                                        new Property(
                                                            'code',
                                                            'Element info',
                                                            element.code,
                                                            true,
                                                            'Property.Code',
                                                            'Property.ElementInfo'
                                                        )
                                                    );
                                                } else if (element.information != null) {
                                                    hsbentity.addProperty(
                                                        new Property(
                                                            'information',
                                                            'Wall info',
                                                            element.information,
                                                            true,
                                                            'Property.Information',
                                                            'Property.WallInfo'
                                                        )
                                                    );
                                                } else if (element.subtype != null) {
                                                    hsbentity.addProperty(
                                                        new Property(
                                                            'subtype',
                                                            'Wall info',
                                                            element.subtype,
                                                            true,
                                                            'Property.Subtype',
                                                            'Property.WallInfo'
                                                        )
                                                    );
                                                } else if (element.quantity != null) {
                                                    hsbentity.addProperty(
                                                        new Property(
                                                            'quantity',
                                                            'Wall info',
                                                            element.quantity,
                                                            true,
                                                            'Property.Quantity',
                                                            'Property.WallInfo'
                                                        )
                                                    );
                                                }
                                            }
                                        );

                                        if (wallentity) {
                                            wallentity.forEach(
                                                (wall: {
                                                    ptorg: {
                                                        x: number | undefined;
                                                        y: number | undefined;
                                                        z: number | undefined;
                                                    } | null;
                                                    vecx: {
                                                        x: number | undefined;
                                                        y: number | undefined;
                                                        z: number | undefined;
                                                    } | null;
                                                    vecy: {
                                                        x: number | undefined;
                                                        y: number | undefined;
                                                        z: number | undefined;
                                                    } | null;
                                                    vecz: {
                                                        x: number | undefined;
                                                        y: number | undefined;
                                                        z: number | undefined;
                                                    } | null;
                                                    instancewidth: null;
                                                    baseheight: null;
                                                }) => {
                                                    _self.setEntityData(hsbentity, wall, hsbModel);

                                                    if (wall.ptorg != null) {
                                                        hsbentity.coordSys.ptOrg =
                                                            new THREE.Vector3(
                                                                wall.ptorg.x,
                                                                wall.ptorg.y,
                                                                wall.ptorg.z
                                                            ).multiplyScalar(unitFactor);
                                                    } else if (wall.vecx != null) {
                                                        hsbentity.coordSys.vecX = new THREE.Vector3(
                                                            wall.vecx.x,
                                                            wall.vecx.y,
                                                            wall.vecx.z
                                                        );
                                                    }
                                                    if (wall.vecy != null) {
                                                        hsbentity.coordSys.vecY = new THREE.Vector3(
                                                            wall.vecy.x,
                                                            wall.vecy.y,
                                                            wall.vecy.z
                                                        );
                                                    }
                                                    if (wall.vecz != null) {
                                                        hsbentity.coordSys.vecZ = new THREE.Vector3(
                                                            wall.vecz.x,
                                                            wall.vecz.y,
                                                            wall.vecz.z
                                                        );
                                                    }

                                                    if (wall.instancewidth != null) {
                                                        hsbentity.addProperty(
                                                            new Property(
                                                                'instancewidth',
                                                                'Wall info',
                                                                wall.instancewidth,
                                                                true,
                                                                'Property.Instancewidth',
                                                                'Property.WallInfo'
                                                            )
                                                        );
                                                    } else if (wall.baseheight != null) {
                                                        hsbentity.addProperty(
                                                            new Property(
                                                                'baseheight',
                                                                'Wall info',
                                                                wall.baseheight,
                                                                true,
                                                                'Property.Baseheight',
                                                                'Property.WallInfo'
                                                            )
                                                        );
                                                    }
                                                }
                                            );
                                        }
                                    }
                                }
                            );
                        }
                    }
                );
            } else if (
                entityname &&
                ['elementroof', 'elementroofsip', 'elementgeneric'].includes(entityname)
            ) {
                hsbentity =
                    entityname === 'elementroofsip'
                        ? new ElementRoofSIP(0, 0)
                        : new ElementRoof(0, 0);

                if (entityname === 'elementgeneric') {
                    hsbentity = new ElementWall(0, 0);
                }

                hsbMap.arr.forEach(
                    (elementroof: {
                        beamspacing: null;
                        beamdirection: null;
                        insulation: null;
                        pttext: {
                            x: number | undefined;
                            y: number | undefined;
                            z: number | undefined;
                        } | null;
                        nm: string;
                        arr: any[];
                    }) => {
                        if (elementroof.beamspacing != null) {
                            hsbentity.addProperty(
                                new Property(
                                    'beamspacing',
                                    'Info',
                                    elementroof.beamspacing,
                                    true,
                                    'Property.Beamspacing',
                                    'Property.Info'
                                )
                            );
                        } else if (elementroof.beamdirection != null) {
                            hsbentity.addProperty(
                                new Property(
                                    'beamdirection',
                                    'Info',
                                    elementroof.beamdirection,
                                    true,
                                    'Property.Beamdirection',
                                    'Property.Info'
                                )
                            );
                        } else if (elementroof.insulation != null) {
                            hsbentity.addProperty(
                                new Property(
                                    'insulation',
                                    'Info',
                                    elementroof.insulation,
                                    true,
                                    'Property.Insulation',
                                    'Property.Info'
                                )
                            );
                        } else if (elementroof.pttext != null) {
                            hsbentity.ptarrow = new THREE.Vector3(
                                elementroof.pttext.x,
                                elementroof.pttext.y,
                                elementroof.pttext.z
                            );
                        }
                        if (elementroof.nm && elementroof.nm.toLowerCase() === 'element') {
                            var roofentity;

                            elementroof.arr.forEach(
                                (element: {
                                    nm: string;
                                    arr: any[];
                                    ptorg: {
                                        x: number | undefined;
                                        y: number | undefined;
                                        z: number | undefined;
                                    } | null;
                                    vecx: {
                                        x: number | undefined;
                                        y: number | undefined;
                                        z: number | undefined;
                                    } | null;
                                    vecy: {
                                        x: number | undefined;
                                        y: number | undefined;
                                        z: number | undefined;
                                    } | null;
                                    vecz: {
                                        x: number | undefined;
                                        y: number | undefined;
                                        z: number | undefined;
                                    } | null;
                                    posnum: null;
                                    number: null;
                                    code: null;
                                    information: null;
                                    subtype: null;
                                    quantity: null;
                                }) => {
                                    // For elementroofsip, we need to get the entity from eroofplane/toolent/entity
                                    if (entityname === 'elementroofsip') {
                                        if (
                                            element.nm &&
                                            element.nm.toLowerCase() === 'eroofplane'
                                        ) {
                                            element.arr.forEach(
                                                (roofplane: { nm: string; arr: any[] }) => {
                                                    if (
                                                        roofplane.nm &&
                                                        roofplane.nm.toLowerCase() === 'toolent'
                                                    ) {
                                                        roofplane.arr.forEach(
                                                            (toolent: { nm: string }) => {
                                                                if (
                                                                    toolent.nm &&
                                                                    toolent.nm.toLowerCase() ===
                                                                        'entity'
                                                                ) {
                                                                    roofentity = toolent;
                                                                }
                                                            }
                                                        );
                                                    }
                                                }
                                            );
                                        }
                                    }

                                    if (element.nm && element.nm.toLowerCase() === 'entity') {
                                        // Process after we've loaded all other values.
                                        roofentity = element;
                                    }

                                    if (element.ptorg != null) {
                                        hsbentity.coordSys.ptOrg = new THREE.Vector3(
                                            element.ptorg.x,
                                            element.ptorg.y,
                                            element.ptorg.z
                                        ).multiplyScalar(unitFactor);
                                    } else if (element.vecx != null) {
                                        hsbentity.coordSys.vecX = new THREE.Vector3(
                                            element.vecx.x,
                                            element.vecx.y,
                                            element.vecx.z
                                        );
                                    } else if (element.vecy != null) {
                                        hsbentity.coordSys.vecY = new THREE.Vector3(
                                            element.vecy.x,
                                            element.vecy.y,
                                            element.vecy.z
                                        );
                                    } else if (element.vecz != null) {
                                        hsbentity.coordSys.vecZ = new THREE.Vector3(
                                            element.vecz.x,
                                            element.vecz.y,
                                            element.vecz.z
                                        );
                                    } else if (element.posnum != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'posnum',
                                                'Element info',
                                                element.posnum,
                                                true,
                                                'Property.Posnum',
                                                'Property.ElementInfo'
                                            )
                                        );
                                    } else if (element.number != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'number',
                                                'Element info',
                                                element.number,
                                                true,
                                                'Property.Number',
                                                'Property.ElementInfo'
                                            )
                                        );
                                        hsbentity.number = element.number;
                                    } else if (element.code != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'code',
                                                'Element info',
                                                element.code,
                                                true,
                                                'Property.Code',
                                                'Property.ElementInfo'
                                            )
                                        );
                                    } else if (element.information != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'information',
                                                'Wall info',
                                                element.information,
                                                true,
                                                'Property.Information',
                                                'Property.WallInfo'
                                            )
                                        );
                                    } else if (element.subtype != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'subtype',
                                                'Wall info',
                                                element.subtype,
                                                true,
                                                'Property.Subtype',
                                                'Property.WallInfo'
                                            )
                                        );
                                    } else if (element.quantity != null) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'quantity',
                                                'Wall info',
                                                element.quantity,
                                                true,
                                                'Property.Quantity',
                                                'Property.WallInfo'
                                            )
                                        );
                                    }
                                }
                            );

                            if (roofentity) {
                                _self.setEntityData(hsbentity, roofentity, hsbModel);
                            }
                        }
                    }
                );
            } else if (
                entityname &&
                [
                    'beam',
                    'sheet',
                    'sheet',
                    'sip',
                    'massgroup',
                    'masselement',
                    'tslinst',
                    'fastenerassemblyent',
                    'collectionentity',
                    'trussentity',
                    'metalpartcollectionent',
                    'blockreference',
                    'openingsf',
                    'opening',
                ].includes(entityname)
            ) {
                hsbentity = _self.addEntityTypeData(hsbProject, hsbModel, hsbMap);

                if (!hsbentity) {
                    if (entityname === 'beam' || entityname === 'sheet' || entityname === 'sip') {
                        switch (entityname) {
                            case 'sheet':
                                hsbentity = new Sheet('0', '0', 0);
                                break;
                            case 'sip':
                                hsbentity = new Sip('0', '0', 0);
                                break;
                            case 'beam':
                                hsbentity = new Beam(0, '0', 0);
                                break;
                        }

                        hsbMap.arr.forEach((elementbeam: any) => {
                            if (elementbeam.nm && elementbeam.nm.toLowerCase() === 'genbeam') {
                                elementbeam.arr.forEach(
                                    (genbeam: {
                                        posnum: null;
                                        name: null;
                                        grade: null;
                                        information: null;
                                        label: null;
                                        material: null;
                                        sublabel: null;
                                        sublabel2: null;
                                        beamcode: null;
                                        hsbid: null;
                                        type: string | any[] | null;
                                        dh: number | null;
                                        dw: number | null;
                                        dl: number | null;
                                    }) => {
                                        _self.setEntityData(hsbentity, genbeam, hsbModel);

                                        if (genbeam.posnum != null) {
                                            hsbentity.posnum = genbeam.posnum;
                                            hsbentity.addProperty(
                                                new Property(
                                                    'posnum',
                                                    'Data',
                                                    genbeam.posnum,
                                                    true,
                                                    'Property.Posnum',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.name != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'name',
                                                    'Data',
                                                    genbeam.name,
                                                    true,
                                                    'Property.Name',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.grade != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'grade',
                                                    'Data',
                                                    genbeam.grade,
                                                    true,
                                                    'Property.Grade',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.information != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'information',
                                                    'Data',
                                                    genbeam.information,
                                                    true,
                                                    'Property.Information',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.label != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'label',
                                                    'Data',
                                                    genbeam.label,
                                                    true,
                                                    'Property.Label',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.material != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'material',
                                                    'Data',
                                                    genbeam.material,
                                                    true,
                                                    'Property.Material',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.sublabel != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'sublabel',
                                                    'Data',
                                                    genbeam.sublabel,
                                                    true,
                                                    'Property.Sublabel',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.sublabel2 != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'sublabel2',
                                                    'Data',
                                                    genbeam.sublabel2,
                                                    true,
                                                    'Property.Sublabel2',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.beamcode != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'beamcode',
                                                    'Data',
                                                    genbeam.beamcode,
                                                    true,
                                                    'Property.Beamcode',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.hsbid != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'hsbid',
                                                    'Data',
                                                    genbeam.hsbid,
                                                    true,
                                                    'Property.Hsbid',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.type != null) {
                                            var val = genbeam.type.slice(2);
                                            hsbentity.addProperty(
                                                new Property(
                                                    'type',
                                                    'Data',
                                                    val,
                                                    true,
                                                    'Property.Type',
                                                    'Property.Data'
                                                )
                                            );
                                        } else if (genbeam.dh != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'dh',
                                                    'Geometry',
                                                    genbeam.dh * unitFactor,
                                                    true,
                                                    'Property.Height',
                                                    'Property.Geometry'
                                                )
                                            );
                                        } else if (genbeam.dw != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'dw',
                                                    'Geometry',
                                                    genbeam.dw * unitFactor,
                                                    true,
                                                    'Property.Width',
                                                    'Property.Geometry'
                                                )
                                            );
                                        } else if (genbeam.dl != null) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    'dl',
                                                    'Geometry',
                                                    genbeam.dl * unitFactor,
                                                    true,
                                                    'Property.Length',
                                                    'Property.Geometry'
                                                )
                                            );
                                        }
                                    }
                                );

                                // Check if coordsys has been set.
                                elementbeam.arr.forEach(
                                    (genbeam: {
                                        ptcen: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        vecx: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        vecy: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                        vecz: {
                                            x: number | undefined;
                                            y: number | undefined;
                                            z: number | undefined;
                                        } | null;
                                    }) => {
                                        if (genbeam.ptcen != null) {
                                            hsbentity.coordSys.ptOrg = new Vector3(
                                                genbeam.ptcen.x,
                                                genbeam.ptcen.y,
                                                genbeam.ptcen.z
                                            ).multiplyScalar(unitFactor);
                                        } else if (genbeam.vecx != null) {
                                            hsbentity.coordSys.vecX = new Vector3(
                                                genbeam.vecx.x,
                                                genbeam.vecx.y,
                                                genbeam.vecx.z
                                            );
                                        } else if (genbeam.vecy != null) {
                                            hsbentity.coordSys.vecY = new Vector3(
                                                genbeam.vecy.x,
                                                genbeam.vecy.y,
                                                genbeam.vecy.z
                                            );
                                        } else if (genbeam.vecz != null) {
                                            hsbentity.coordSys.vecZ = new Vector3(
                                                genbeam.vecz.x,
                                                genbeam.vecz.y,
                                                genbeam.vecz.z
                                            );
                                        }
                                    }
                                );
                            } else if (elementbeam.woodgraindirection) {
                                let grainDirection = new Vector3(
                                    elementbeam.woodgraindirection.x,
                                    elementbeam.woodgraindirection.y,
                                    elementbeam.woodgraindirection.z
                                );
                                if (!grainDirection.equals(new Vector3())) {
                                    hsbentity.woodgraindirection = grainDirection;
                                }
                            } else if (elementbeam.style) {
                                hsbentity.addProperty(
                                    new Property(
                                        'style',
                                        'Data',
                                        elementbeam.style,
                                        true,
                                        'Property.Style',
                                        'Property.Data'
                                    )
                                );
                            }
                        });
                    } else if (entityname == 'openingsf' || entityname == 'opening') {
                        hsbentity = new Opening('0', '0', 0);

                        hsbMap.arr.forEach((openingent: any) => {
                            _self.setEntityData(hsbentity, openingent, hsbModel);
                            if (openingent.nm?.toLowerCase() == 'opening') {
                                _self.setOpeningEntityData(hsbentity, openingent, hsbModel);
                            }
                        });
                    } else if (entityname === 'massgroup' || entityname === 'masselement') {
                        hsbentity =
                            entityname === 'massgroup'
                                ? new MassGroup('0', '0', 0)
                                : new MassElement('0', '0', 0);

                        hsbMap.arr.forEach((massgroup: { posnum: null }) => {
                            _self.setEntityData(hsbentity, massgroup, hsbModel);

                            if (massgroup.posnum != null) {
                                hsbentity.posnum = massgroup.posnum;
                                hsbentity.addProperty(
                                    new Property(
                                        'posnum',
                                        'Data',
                                        massgroup.posnum,
                                        true,
                                        'Property.Posnum',
                                        'Property.Data'
                                    )
                                );
                            }
                        });

                        hsbMap.arr.forEach(
                            (massgroup: {
                                ptorg: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                vecx: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                vecy: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                vecz: {
                                    x: number | undefined;
                                    y: number | undefined;
                                    z: number | undefined;
                                } | null;
                                posnum: null;
                            }) => {
                                if (!hsbentity.coordSys.ptOrg && massgroup.ptorg != null) {
                                    hsbentity.coordSys.ptOrg = new THREE.Vector3(
                                        massgroup.ptorg.x,
                                        massgroup.ptorg.y,
                                        massgroup.ptorg.z
                                    ).multiplyScalar(unitFactor);
                                } else if (!hsbentity.coordSys.vecX && massgroup.vecx != null) {
                                    hsbentity.coordSys.vecX = new THREE.Vector3(
                                        massgroup.vecx.x,
                                        massgroup.vecx.y,
                                        massgroup.vecx.z
                                    );
                                } else if (!hsbentity.coordSys.vecY && massgroup.vecy != null) {
                                    hsbentity.coordSys.vecY = new THREE.Vector3(
                                        massgroup.vecy.x,
                                        massgroup.vecy.y,
                                        massgroup.vecy.z
                                    );
                                } else if (!hsbentity.coordSys.vecZ && massgroup.vecz != null) {
                                    hsbentity.coordSys.vecZ = new THREE.Vector3(
                                        massgroup.vecz.x,
                                        massgroup.vecz.y,
                                        massgroup.vecz.z
                                    );
                                } else if (massgroup.posnum != null) {
                                    hsbentity.posnum = massgroup.posnum;
                                    hsbentity.addProperty(
                                        new Property(
                                            'posnum',
                                            'Data',
                                            massgroup.posnum,
                                            true,
                                            'Property.Posnum',
                                            'Property.Data'
                                        )
                                    );
                                }
                            }
                        );
                    } else if (entityname === 'metalpartcollectionent') {
                        hsbentity = new MetalPartCollectionEnt(0, '0', 0);

                        hsbMap.arr.forEach((collectionent: any) => {
                            if (
                                collectionent.nm &&
                                collectionent.nm.toLowerCase() === 'collectionentity'
                            ) {
                                _self.setCollectionEntityData(hsbentity, collectionent, hsbModel);
                            }
                        });
                    } else if (entityname === 'blockreference') {
                        hsbentity = new BlockReference(0, '0', 0);
                        _self.setBlockReferenceEntityData(hsbentity, hsbMap, hsbModel);
                    } else if (entityname === 'collectionentity') {
                        hsbentity = new CollectionEntity(0);
                        _self.setCollectionEntityData(hsbentity, hsbMap, hsbModel);
                    } else if (entityname === 'tslinst' || entityname === 'fastenerassemblyent') {
                        if (entityname === 'tslinst') {
                            hsbentity = new TslInst(0, '0', 0);

                            hsbMap.arr.forEach((tslinst: { nm: string; arr: any[] }) => {
                                if (tslinst.nm && tslinst.nm.toLowerCase() === 'toolent') {
                                    tslinst.arr.forEach((toolent: { nm: string; arr: any[] }) => {
                                        if (toolent.nm && toolent.nm.toLowerCase() === 'entity') {
                                            toolent.arr.forEach(
                                                (ent: { nm: string; arr: any[] }) => {
                                                    if (
                                                        ent.nm &&
                                                        ent.nm.toLowerCase() === 'mapx[]'
                                                    ) {
                                                        ent.arr.forEach((mapx: { arr: any[] }) => {
                                                            mapx.arr.forEach(
                                                                (mapxvals: { nm: string }) => {
                                                                    if (
                                                                        mapxvals.nm &&
                                                                        mapxvals.nm.toLowerCase() ===
                                                                            'hsb_nestingparent'
                                                                    ) {
                                                                        hsbentity = new Nesting(0);
                                                                    } else if (
                                                                        mapxvals.nm &&
                                                                        mapxvals.nm.toLowerCase() ===
                                                                            'hsb_stackingparent'
                                                                    ) {
                                                                        hsbentity = new Stack(0);
                                                                    } else if (
                                                                        mapxvals.nm &&
                                                                        mapxvals.nm.toLowerCase() ===
                                                                            'hsb_truckparent'
                                                                    ) {
                                                                        hsbentity = new Stack(0);
                                                                    }
                                                                }
                                                            );
                                                        });
                                                    }
                                                }
                                            );
                                        }
                                    });
                                }
                            });
                        } else if (entityname === 'fastenerassemblyent') {
                            hsbentity = new FastenerAssemblyEnt(0, '0', 0);
                        }
                        hsbMap.arr.forEach(
                            (tslinst: { nm: string; arr: any[]; scriptname: any }) => {
                                if (tslinst.nm && tslinst.nm.toLowerCase() === 'toolent') {
                                    tslinst.arr.forEach(
                                        (toolent: { posnum: null; nm: string; arr: any[] }) => {
                                            _self.setEntityData(hsbentity, toolent, hsbModel);

                                            if (toolent.posnum != null) {
                                                hsbentity.posnum = toolent.posnum;
                                                hsbentity.addProperty(
                                                    new Property(
                                                        'posnum',
                                                        'Data',
                                                        toolent.posnum,
                                                        true,
                                                        'Property.Posnum',
                                                        'Property.Data'
                                                    )
                                                );
                                            } else if (
                                                toolent.nm &&
                                                toolent.nm.toLowerCase() === 'handle[]'
                                            ) {
                                                hsbentity.entities = [];
                                                toolent.arr.forEach((handle: { ty: string }) => {
                                                    if (handle.ty === 'hdl') {
                                                        // hsbentity.entities.push(handle.han);
                                                    }
                                                });
                                            }
                                        }
                                    );
                                } else if (tslinst.scriptname) {
                                    hsbentity.scriptname = tslinst.scriptname;

                                    if (hsbentity.scriptname) {
                                        hsbentity.addProperty(
                                            new Property(
                                                'scriptname',
                                                'Data',
                                                hsbentity.scriptname,
                                                true,
                                                'Property.Scriptname',
                                                'Property.Data'
                                            )
                                        );
                                    }
                                } else if (
                                    tslinst.nm &&
                                    tslinst.arr &&
                                    tslinst.nm.toLowerCase() === 'map'
                                ) {
                                    tslinst.arr.forEach(
                                        (propset: {
                                            ty: string;
                                            hasOwnProperty: (arg0: string) => any;
                                        }) => {
                                            if (propset.ty !== 'key') {
                                                for (var prop in propset) {
                                                    if (propset.hasOwnProperty(prop)) {
                                                        if (prop !== 'ty' && prop !== 'ut') {
                                                            //hsbentity.addProperty(new Property(prop, "inventory", val));
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    );
                                } else if (
                                    tslinst.nm &&
                                    tslinst.arr &&
                                    tslinst.nm.toLowerCase() === 'propstring[]'
                                ) {
                                    tslinst.arr.forEach((prop: { arr: any[] }) => {
                                        var name = prop.arr.filter(function (e: { strname: any }) {
                                            return e.strname;
                                        })[0];
                                        var value = prop.arr.filter(function (e: {
                                            strvalue: any;
                                        }) {
                                            return e.strvalue;
                                        })[0];

                                        if (name && value) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    name[Object.keys(name)[0]],
                                                    'data',
                                                    value[Object.keys(value)[0]],
                                                    false
                                                )
                                            );
                                        }
                                    });
                                } else if (
                                    tslinst.nm &&
                                    tslinst.arr &&
                                    tslinst.nm.toLowerCase() === 'propint[]'
                                ) {
                                    tslinst.arr.forEach((prop: { arr: any[] }) => {
                                        var name = prop.arr.filter(function (e: { strname: any }) {
                                            return e.strname;
                                        })[0];
                                        var value = prop.arr.filter(function (e: { ivalue: any }) {
                                            return e.ivalue;
                                        })[0];

                                        if (name && value) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    name[Object.keys(name)[0]],
                                                    'data',
                                                    value[Object.keys(value)[0]],
                                                    false
                                                )
                                            );
                                        }
                                    });
                                } else if (
                                    tslinst.nm &&
                                    tslinst.arr &&
                                    tslinst.nm.toLowerCase() === 'propdouble[]'
                                ) {
                                    tslinst.arr.forEach((prop: { arr: any[] }) => {
                                        var name = prop.arr.filter(function (e: { stname: any }) {
                                            return e.stname;
                                        })[0];
                                        var value = prop.arr.filter(function (e: { ivalue: any }) {
                                            return e.ivalue;
                                        })[0];

                                        if (name && value) {
                                            hsbentity.addProperty(
                                                new Property(
                                                    name[Object.keys(name)[0]],
                                                    'data',
                                                    value[Object.keys(value)[0]],
                                                    false
                                                )
                                            );
                                        }
                                    });
                                }
                            }
                        );
                    }
                }
            }

            if (hsbentity && hsbentity.handle !== 0) {
                hsbentity.hsbmodel = hsbModel;
                hsbModel.Entities.push(hsbentity);

                if (hsbProject) {
                    // Add to fast lookup table.
                    if (!hsbProject.EntityTable[hsbModel.modelId]) {
                        hsbProject.EntityTable[hsbModel.modelId] = {};
                    }

                    hsbProject.EntityTable[hsbModel.modelId][hsbentity.handle] = hsbentity;
                }
            }
        });
    }

    addEntityToModel(
        hsbProject: HsbProject,
        hsbModel: HsbModel,
        hsbentity: Group | MultiWall | Truck | GenericModel
    ) {
        if (hsbentity && hsbentity.handle !== '0') {
            hsbentity.hsbmodel = hsbModel;
            hsbModel.Entities.push(hsbentity);

            if (hsbProject) {
                // Add to fast lookup table.
                if (!hsbProject.EntityTable[hsbModel.modelId]) {
                    hsbProject.EntityTable[hsbModel.modelId] = {};
                }

                hsbProject.EntityTable[hsbModel.modelId][hsbentity.handle] = hsbentity;
            }
        }
    }

    convertKeystoLowerCase(obj: any) {
        var _self = this;
        var ret: any = null;
        if (typeof obj === 'string' || typeof obj === 'number') return obj;
        else if (obj.push) ret = [];
        else ret = {};
        for (var key in obj)
            ret[String(key).toLowerCase()] = _self.convertKeystoLowerCase(obj[key]);
        return ret;
    }

    createCadModel(mmproject: { arr: any[] }, hsbModel: HsbModel, hsbProject: HsbProject | null) {
        var _self = this;
        var loadModel = function (topobject: { arr: any[] }) {
            topobject.arr.forEach((subobject: { nm: string; arr: any[] }) => {
                var groupmap = topobject.arr.filter((e: { nm: string }) => {
                    return e.nm && e.nm.toLowerCase() === 'group[]';
                });
                var collections = topobject.arr.filter(function (e: { nm: string }) {
                    return e.nm && e.nm.toLowerCase() === 'collections';
                })[0];
                var objectname = subobject.nm ? subobject.nm.toLowerCase() : null;

                if (groupmap.length === 1) {
                    _self.loadGroups(groupmap[0], hsbProject, hsbModel);
                }

                if (collections) {
                    // _self.loadCollections(collections, hsbProject, hsbModel);
                }

                if (objectname === 'model') {
                    if (hsbProject && hsbProject.projectSource.toLowerCase() === 'ifc') {
                        var ifcdataroot = _self.createIFCMMNodeModel(hsbModel, hsbProject);
                        var confignode = hsbProject.nodeModel.getconfigNode();
                        _self._treeconfig = null;

                        if (confignode && confignode.metadata.data.treeconfig) {
                            _self._treeconfig = confignode.metadata.data.treeconfig;
                        }

                        subobject.arr.forEach((ifcEntity: any) => {
                            _self.loadIFCEntities(ifcEntity, ifcdataroot, hsbModel, hsbProject);
                        });
                    } else if (hsbProject) {
                        _self.loadEntities(subobject, hsbModel, hsbProject);
                    }
                }
            });
        };

        mmproject.arr.forEach((subobject: { nm: string; arr: any[] }) => {
            var objectname = subobject.nm ? subobject.nm.toLowerCase() : null;

            if (objectname === 'model') {
                loadModel(mmproject);
            } else if (objectname === 'subproject[]') {
                subobject.arr.forEach((subproject: any) => {
                    loadModel(subproject);
                });
            }
        });

        if (hsbProject) {
            _self.checkforTruckLoading(hsbModel, hsbProject);

            _self.checkforNesting(hsbModel, hsbProject);
            // Suppress for now...
            //_self.checkforMultiWalls(hsbModel, hsbProject);
        }

        _self.prepareHsbModel(hsbModel);
    }

    createDatabaseInfo(hsbProject: {
        datastoreFolder: any;
        nodeModel: {
            getChildNodes: (arg0: any) => any;
            ItemsRoot: any;
            ModelRoot: any;
            findGroupSiteRootNode: (arg0: any, arg1: any) => any;
            findGroupChildNodeByPathId: (arg0: any, arg1: any) => any;
            findAllChildNodesofNode: (arg0: any, arg1: any) => any;
        };
    }) {
        var datastorefolder = hsbProject.datastoreFolder;
        var allnodes = hsbProject.nodeModel.getChildNodes(hsbProject.nodeModel.ItemsRoot); // WLM NODEITEM
        let modelroot = hsbProject.nodeModel.ModelRoot;
        let itemsroot = hsbProject.nodeModel.ItemsRoot;

        allnodes.forEach(function (node: {
            [x: string]: any;
            pathid: string;
            metadata: {
                data: { itemtype: string; references: any[]; reference: any; properties: any };
            };
            dbId: any;
            props: { [x: string]: any };
        }) {
            if (!node.pathid) return;
            var setIndexes = function (reference: { pathid: any; model: any }) {
                // Need to find the node in either model.
                if (reference.pathid) {
                    var pathid;
                    pathid = reference.pathid;
                    let modeldata;

                    if (reference.model) {
                        let drawingroot = hsbProject.nodeModel.findGroupSiteRootNode(
                            modelroot,
                            reference.model
                        );
                        if (drawingroot) {
                            modeldata = hsbProject.nodeModel.findGroupChildNodeByPathId(
                                drawingroot,
                                pathid
                            );

                            var childnodes = hsbProject.nodeModel.findAllChildNodesofNode(
                                modeldata,
                                modeldata
                            );
                            childnodes.forEach(function (childnode: { [x: string]: any }) {
                                childnode[node.metadata.data.itemtype + 'ID'] = node.dbId;
                            });
                        }
                    } else {
                        modeldata = hsbProject.nodeModel.findGroupChildNodeByPathId(
                            itemsroot,
                            pathid
                        );

                        if (modeldata) {
                            node[modeldata.metadata.data.itemtype + 'ID'] = modeldata.dbId;
                        }
                    }
                }
            };

            // Set the references
            if (node.metadata.data.references) {
                node.metadata.data.references.forEach((reference: any) => {
                    setIndexes(reference);
                });
            } else if (node.metadata.data.reference) {
                setIndexes(node.metadata.data.reference);
            }

            // Node hsb the properties into the props table.
            if (node.metadata.data.properties) {
                var properties = node.metadata.data.properties;
                node.props = {};

                if (Array.isArray(properties)) {
                    properties.forEach(function (property) {
                        node.props[property.data.key] = {};
                        node.props[property.data.key] = property.data;
                    });
                } else {
                    Object.keys(properties).forEach((propertyKey) => {
                        var property = properties[propertyKey];
                        node.props[propertyKey] = {};
                        node.props[propertyKey] = property;
                    });
                }
            }
        });
    }

    createHsbModel(
        metadata: { data: { success: string }; filepath: string },
        node: { metadata: { data: any } },
        hsbProject: HsbProject
    ) {
        var _self = this;

        if (metadata.data.success !== 'deleted' || !node) {
            var mmproject = _self.convertKeystoLowerCase(metadata.data);
            var projectinfomap = mmproject.arr.filter((e: { nm: string }) => {
                return e.nm && e.nm.toLowerCase() === 'projectinfo';
            });

            var path = metadata.filepath.split('/').pop()?.split('.')!;
            let extension = path.pop();
            let filename = path[0];

            let nodeData = node.metadata.data;

            if (nodeData.properties && nodeData.properties['modelName']) {
                filename = nodeData.properties['modelName'].value;
            } else if (nodeData.modelname) {
                filename = nodeData.modelname;
            }

            _self.setProjectInfo(projectinfomap[0], filename);
            var { drawingName, unit } = _self.loadProjectInfo(projectinfomap[0], hsbProject);
            let modelId = drawingName;

            if (nodeData.properties && nodeData.properties['modelId']) {
                modelId = nodeData.properties['modelId'].value;
            } else if (nodeData.modelId) {
                modelId = nodeData.modelId;
            }

            if (drawingName && !hsbProject.HsbModels[modelId]) {
                hsbProject.HsbModels[modelId] = new HsbModel('');
                hsbProject.HsbModels[modelId].modelId = modelId;

                hsbProject.HsbModels[modelId].DrawingName = drawingName;
                hsbProject.HsbModels[modelId].Units = new Units(unit ?? 'mm');
                hsbProject.HsbModels[modelId].FilePath = metadata.filepath;
                hsbProject.HsbModels[modelId].projectSource = hsbProject.projectSource;
                hsbProject.HsbModels[modelId].Node = node;

                try {
                    _self.createCadModel(mmproject, hsbProject.HsbModels[modelId], hsbProject);
                    _self.checkForHFDM(projectinfomap[0], hsbProject.HsbModels[modelId]);
                    _self.createMMNodeModel(hsbProject.HsbModels[modelId], hsbProject);
                    //_self.removeEmptyGroupNodes(hsbProject.HsbModels[modelId], hsbProject);
                    _self.getFilterInfo(hsbProject);
                } catch (err) {
                    console.error(err);
                }
            }
        }
    }

    createDocNodeList(hsbProject: HsbProject) {
        // Get all Item nodes
        var allitemnodes = hsbProject.nodeModel.getChildNodes(hsbProject.nodeModel.ItemsRoot); // WLM NODEITEM
        var dataroot = hsbProject.nodeModel.findGroupChildNode(hsbProject.nodeModel.root, 'data');
        if (!dataroot) return;
        hsbProject.docNodes = [];

        allitemnodes.forEach(function (node) {
            // For special modelinfo node we need to reference this node back to it's
            if (node.metadata.data.itemtype === NodeType.ModelInfo) {
                var refnode = hsbProject.nodeModel.findGroupChildNodesByRefPathId(
                    dataroot as Node,
                    node.metadata.data.reference.model,
                    node.metadata.data.reference.pathid
                )[0];
                if (refnode) {
                    var datalink: any = null;
                    if (node.metadata.data.properties) {
                        datalink = node.metadata.data.properties.find(
                            (e: { data: { datatype: { type: string } } }) =>
                                e.data?.datatype?.type === 'url'
                        );
                    }
                    var docFiles = hsbProject.docFiles.filter(function (e) {
                        return e.path.indexOf(node.pathid + '/') === 0;
                    });
                    if (docFiles.length > 0 || datalink?.data?.value) {
                        hsbProject.docNodes.push({ refnode: refnode, modelinfonode: node });
                        node.docFiles = docFiles;
                        if (docFiles.length > 0) {
                            refnode.docFiles = docFiles;
                        }
                    }
                }
            } else {
                // For all other item node - add a docfiles node with all the files for reference.
                var docFiles = hsbProject.docFiles.filter(function (e) {
                    return e.path.indexOf(node.pathid + '/') === 0;
                });
                if (docFiles.length > 0) {
                    node.docFiles = docFiles;
                }
            }
        });
    }

    checkDesignReview(hsbProject: {
        HsbModels: { [x: string]: any };
        nodeModel: {
            createNode: (arg0: string, arg1: any) => any;
            addChildNode: (arg0: any, arg1: any) => void;
        };
        EntityTable: { [x: string]: { [x: string]: any } };
    }) {
        // We need check all deleted items and include the entities from previous commits
        let commitModels: any[] = [];

        Object.keys(hsbProject.HsbModels).forEach(function (modelKey) {
            if (hsbProject.HsbModels[modelKey].hfdmCommitData) {
                commitModels.push(hsbProject.HsbModels[modelKey]);
            }
        });

        commitModels.sort(function (a, b) {
            return a.hfdmCommitData.timeStamp - b.hfdmCommitData.timeStamp;
        });

        if (commitModels.length > 1) {
            for (let index = commitModels.length - 1; index >= 0; index--) {
                let model = commitModels[index];

                if (
                    model.hfdmCommitData &&
                    model.hfdmCommitData.changeSets &&
                    model.hfdmCommitData.changeSets['deleted']
                ) {
                    model.hfdmCommitData.changeSets['deleted'].forEach((handle: any) => {
                        // Try and find this deleted handle from the previous commit models
                        for (let index1 = index - 1; index1 >= 0; index1--) {
                            let model1 = commitModels[index1];

                            let foundEntity = model1.findEntity(handle);
                            if (foundEntity) {
                                var newEntity = foundEntity.clone();

                                newEntity.hsbmodel = model;
                                newEntity.hfdmAction = 'deleted';

                                var itemNode = hsbProject.nodeModel.createNode(
                                    'Entity',
                                    foundEntity.node.parent
                                );
                                hsbProject.nodeModel.addChildNode(
                                    foundEntity.node.parent,
                                    itemNode
                                );
                                itemNode.hsbmodel = model;
                                itemNode.entity = newEntity;
                                itemNode.entity.node = itemNode;
                                itemNode.metadata.data = {
                                    nodetype: NodeType.Geom,
                                    itemtype: NodeType.Geom,
                                    refitemtype: RefItemType.Parent,
                                    geom: {},
                                };
                                itemNode.entity.hsbmodel = itemNode.hsbmodel;
                                itemNode.pathid = newEntity.handle.toLowerCase();

                                newEntity.node = itemNode;
                                newEntity.needsTranslation = true;
                                newEntity.initialCoordSys = foundEntity.initialCoordSys;

                                hsbProject.EntityTable[model.modelId][foundEntity.handle] =
                                    newEntity;
                                model.Entities.push(newEntity);
                                break;
                            }
                        }
                    });
                }
            }
        }
    }

    loadGeometryData(hsbMakeModels: Node[], hsbProject: HsbProject) {
        let self = this;

        var promise = new Promise(function (resolve, reject) {
            self.loadGeometryDataDB(hsbMakeModels, hsbProject)
                .then(function () {
                    resolve('success');
                })
                .catch(function (err) {
                    return reject(err);
                });
        });

        return promise;
    }

    loadGeometryDataDB(modelnodes: any[], hsbProject: any) {
        var _self = this;

        var promise = new Promise(function (resolve, reject) {
            var activemodels = modelnodes.filter(function (e: {
                metadata: { data: { properties: { [x: string]: { value: boolean } } } };
            }) {
                return (
                    e.metadata.data.properties['active'] &&
                    e.metadata.data.properties['active'].value === true
                );
            });
            var visiblemodels: any[] = [];

            if (_self.activeModelId) {
                visiblemodels = modelnodes.filter(function (e: {
                    metadata: { data: { properties: { [x: string]: { value: any } } } };
                }) {
                    return (
                        e.metadata.data.properties['modelId'] &&
                        e.metadata.data.properties['modelId'].value == _self.activeModelId
                    );
                });
                _self.activeModelId = null;
            } else {
                if (
                    hsbProject.activeProject &&
                    hsbProject.activeProject.sets &&
                    hsbProject.activeProject.sets.length > 0
                ) {
                    // Only use visible flag in the project set.
                    hsbProject.activeProject.sets.forEach((set: { urns: any[] }) => {
                        set.urns.forEach((urn: { visible: any; name: any }) => {
                            if (urn.visible) {
                                var foundmodel = activemodels.filter(function (e: {
                                    metadata: { data: { modelname: any } };
                                }) {
                                    return e.metadata.data.modelname === urn.name;
                                })[0];
                                if (foundmodel) {
                                    visiblemodels.push(foundmodel);
                                }
                            }
                        });
                    });
                } else {
                    visiblemodels = activemodels;
                }
            }

            var promisefunctions: Promise<any>[] = [];

            visiblemodels.forEach(function (node) {
                var promise = new Promise(function (resolve, reject) {
                    if (
                        !node.metadata.data.documents ||
                        node.metadata.data.documents.length === 0
                    ) {
                        return reject(
                            'ProjectLoader.loadGeometryDataDB(): No document data provided!'
                        );
                    }

                    let hsbFiles = node.metadata.data.documents.filter((doc: { path: string }) => {
                        let fileName = doc.path.split('/').pop();
                        return fileName?.indexOf('.json') !== -1;
                    });

                    if (hsbFiles.length !== 1) {
                        return reject('ok, but failed to load model file');
                    }

                    var pathid = hsbFiles[0].path;

                    _self._nodeloader
                        .getFile(pathid)
                        .then(function (filedata) {
                            // Now build the model from this file data
                            _self.createHsbModel(filedata, node, hsbProject);
                            _self.checkDesignReview(hsbProject);

                            resolve('success');
                            // We are done!
                        })
                        .catch(function (err) {
                            reject(err);
                        });
                });
                promisefunctions.push(promise);
            });

            Promise.all(promisefunctions)
                .then(function () {
                    _self.checkDesignReview(hsbProject);
                    resolve('success');
                })
                .catch(function (err) {
                    reject(err);
                });
        });

        return promise;
    }
    /**
     * Creates a text object in world space.
     * @param label The desired text to be displayed
     * @param size The font height
     * @param height
     * @param matrix4
     * @param material
     * @param justification
     * @param position
     */
    createTextObject(
        label: { toString: () => string },
        size: any,
        height: any,
        matrix4: THREE.Matrix4,
        material: any,
        justification: string,
        position: { x: number; y: number; z: number },
        font: any
    ) {
        var text3d = new TextGeometry(label.toString(), {
            size: size,
            height: height,
            curveSegments: 1,
            font: font,
        });

        let text = new THREE.Mesh(text3d, material);
        text.applyMatrix4(matrix4);

        let aabb = new THREE.Box3().setFromObject(text);
        var centerOffset = aabb.max.x - aabb.min.x;

        text.position.x = position.x;
        text.position.y = position.y;
        text.position.z = position.z;

        if (justification === 'centre') {
            centerOffset *= -0.5;
        } else if (justification === 'right') {
            centerOffset *= -1.0;
        } else {
            centerOffset = 0.0;
        }

        text.translateX(centerOffset);

        return text;
    }

    /**
     * Returns a sprite object that will display text that always faces the camera.
     * @param message The desired message to be displayed
     * @param parameters The optional prameters to be set
     */
    createTextSpriteObject(
        message: string,
        parameters: {
            font?: string;
            fontsize?: number;
            canvassize?: number;
            borderThickness?: number;
            fitText?: boolean;
            textcolor?: { r: string; g: string; b: string; a: string };
            borderColor?: { r: string; g: string; b: string; a: string };
            backgroundColor?: { r: string; g: string; b: string; a: string };
        }
    ) {
        var parameters = parameters || {};
        var fontface = parameters.font || 'Monospace';
        var fontsize = parameters.fontsize || 100;
        var canvas = document.createElement('canvas');
        var fitText = parameters.fitText || false;
        var borderThickness = parameters.borderThickness || 3;
        canvas.width = parameters.canvassize ? parameters.canvassize : 300;
        canvas.height = parameters.canvassize ? parameters.canvassize * 0.5 : 150;
        var context = canvas.getContext('2d')!;
        context.font = fontsize + 'px ' + fontface;

        // get size data (height depends only on font size)
        var textWidth = context.measureText(message).width;
        if (fitText) {
            var ratio = canvas.width / textWidth;
            fontsize *= ratio;
            fontsize--;
            context.font = fontsize + 'px ' + fontface;
        }
        textWidth = context.measureText(message).width;

        context.fillStyle = parameters.backgroundColor
            ? 'rgba(' +
              parameters.backgroundColor.r +
              ',' +
              parameters.backgroundColor.g +
              ',' +
              parameters.backgroundColor.b +
              ',' +
              parameters.backgroundColor.a +
              ')'
            : 'rgba(255, 255, 255, 1.0)';
        context.strokeStyle = parameters.borderColor
            ? 'rgba(' +
              parameters.borderColor.r +
              ',' +
              parameters.borderColor.g +
              ',' +
              parameters.borderColor.b +
              ',' +
              parameters.borderColor.a +
              ')'
            : 'rgba(20, 20, 20, 1.0)';

        context.lineWidth = borderThickness;
        function roundRect(
            ctx: CanvasRenderingContext2D,
            x: number,
            y: number,
            w: number,
            h: number,
            r: number
        ) {
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx.lineTo(x + w, y + h - r);
            ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx.lineTo(x + r, y + h);
            ctx.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx.lineTo(x, y + r);
            ctx.quadraticCurveTo(x, y, x + r, y);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
        }
        roundRect(
            context,
            borderThickness / 2,
            borderThickness / 2,
            canvas.width - borderThickness * 2,
            canvas.height - borderThickness * 2,
            8
        );

        // text color
        context.fillStyle = parameters?.textcolor
            ? 'rgba(' +
              parameters.textcolor.r +
              ', ' +
              parameters.textcolor.g +
              ', ' +
              parameters.textcolor.b +
              ', ' +
              parameters.textcolor.a +
              ')'
            : 'rgba(0, 0, 0, 1.0)';
        context.fillText(message, 0, fontsize);

        // canvas contents will be used for a texture
        var texture = new THREE.Texture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.needsUpdate = true;

        var spriteMaterial = new THREE.SpriteMaterial({ map: texture });
        var sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(canvas.width, canvas.height, 1);
        return sprite;
    }
}
